import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  HostBinding,
} from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { EngageActivityService, EngageActivity } from 'src/app/core/services/engage-activity.service';
import { ActivityNotesService } from 'src/app/core/services/activity-notes.service';
import { TwilioSmsService, TwilioMessage } from 'src/app/core/services/twilio-sms.service';
import { ApplicationCacheService } from 'src/app/core/services/application-cache.service';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { EventService } from 'src/app/core/services/event.service';
import { RemindersService } from 'src/app/core/services/reminders.service';
import { BroadcastEventTypes } from 'src/app/core/models/lookup.model';
import { EngageSocketService } from 'src/app/core/services/engage-socket.service';
import { Utils } from 'src/app/core/services/utils';
import { cloneDeep } from 'lodash';
import Swal from 'sweetalert2';
import { UpdateActivityNoteComponent } from 'src/app/shared/components/update-activity-note/update-activity-note.component';
import { MediaPreviewModalComponent } from 'src/app/shared/components/media-preview-modal/media-preview-modal.component';
import { ActivityNote, ActivityNoteDisplay } from 'src/app/core/models/activity-notes.model';
import {
  activityNoteBody,
  isEmptyScheduleCreatedActivity,
} from 'src/app/core/helpers/schedule-activity-note.util';
import { ApiStatus, ReferralApis } from 'src/app/core/services/utils';
import {
  ActivityFilter,
  ActivityFilterCategory,
} from 'src/app/core/models/activity-filter.model';
import { STAGE_OPTIONS } from 'src/app/shared/components/stage-activity/stage-activity.component';
import {
  engageActivityTypeDisplayLabel,
  extractEngageActivityAppointmentDateDisplay,
  extractEngageActivityTimestampIso,
  formatEngageActivityCreatedDateTime,
  humanizeActivityTypeToken,
  isEngageAddedCommentType,
  isEngageAppointmentConfirmedType,
  isGenericReferralOverviewMetaLabel,
} from 'src/app/core/helpers/engage-referral-activity.helper';
import { getAppointmentReferralId } from 'src/app/core/models/admissions.model';
import { EngageEventByReferralItem } from 'src/app/core/models/engage-event.model';
import { AdmissionsService } from 'src/app/core/services/admissions.service';
import { resolveClinicPatientIdDistinctFromReferral } from 'src/app/core/services/engage-patient.service';
import type { WorksheetEngageBadgeDismissChannel } from 'src/app/core/helpers/worksheet-row-action-badges';
import {
  activityTypeUsesNoteIcon,
  isReferralCreatedActivityType,
} from 'src/app/core/helpers/activity-note-nurse-note.util';
import {
  ActivityEncounterSource,
  buildEncounterDosLookupFromActivityNotes,
  formatActivityEncounterDosDisplay,
  formatActivityNoteTitleWithDos,
  normalizeActivityEncounterDosIso,
  resolveActivityEncounterDosIso,
  resolveActivityNoteEncounterDosDisplay,
} from 'src/app/core/helpers/activity-encounter-context.util';

export type TimelineItemSource = 'engage' | 'activity' | 'sms' | 'event';

export interface TimelineItem {
  source: TimelineItemSource;
  sortDate: number;
  data: EngageActivity | ActivityNoteDisplay | TwilioMessage | EngageEventByReferralItem;
}

@Component({
  selector: 'app-unified-activity-feed',
  templateUrl: './unified-activity-feed.component.html',
  styleUrls: ['./unified-activity-feed.component.scss']
})
export class UnifiedActivityFeedComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
  /** When true, timeline bubbles have no card border (e.g. narrow Engage prospect panel). */
  @Input() stripItemBorders = false;
  /** When true, rows span the container width (prospect History — not chat-style right rail). */
  @Input() fullWidthTimeline = false;

  @HostBinding('class.unified-feed--strip-item-borders')
  get stripItemBordersHostClass(): boolean {
    return this.stripItemBorders;
  }

  @HostBinding('class.unified-feed--full-width-timeline')
  get fullWidthTimelineHostClass(): boolean {
    return this.fullWidthTimeline;
  }

  @HostBinding('class.unified-feed--nurse-notes')
  get nurseNotesTimelineHostClass(): boolean {
    return this.nurseNotesTimeline;
  }

  @Input() patientId: string = '';
  @Input() referralId: string = '';
  /** Procedure record id for nurse notes / chart activity (not patient or encounter id). */
  @Input() procedureId: string = '';
  /** Optional encounter id from application cache (Engage / global notes) for procedure-scoped context. */
  @Input() encounterId: string = '';
  @Input() activityFilter: ActivityFilter = { categories: [] };
  /** Procedure nurse notes: vertical timeline with initials avatars and compact cards. */
  @Input() nurseNotesTimeline = false;
  /**
   * Engage timeline GET `filter` query (`unread_message`, `notifications`).
   * When set, category matching is skipped (server already filtered); date range still applies client-side.
   */
  @Input() timelineApiFilters: string[] = [];
  @Output() chatNamesReady = new EventEmitter<string[]>();
  /** Fires when merged timeline rows change (full load, push, socket) — e.g. Engage prospect “Recent activity” mirror. */
  @Output() readonly timelineMerged = new EventEmitter<void>();
  @Output() readonly historyPinsChanged = new EventEmitter<void>();
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('scrollAnchor') scrollAnchor?: ElementRef<HTMLDivElement>;
  @ViewChild('reminderTimeInput') reminderTimeInputRef?: ElementRef<HTMLInputElement>;

  mergedItems: TimelineItem[] = [];
  loggedInUserName: string = '';
  loggedInUserId: string = '';
  isLoading: boolean = false;
  private readonly destroy$ = new Subject<void>();
  private companyUsersMap: Record<string, { firstName: string; lastName: string }> = {};

  // Engage: reminder edit
  editingReminderId: string | null = null;
  editingActivityId: string | null = null;
  editingNurseNoteId: string | null = null;
  editingNurseNoteText = '';
  editingNurseNoteDate = '';
  editingNurseNoteTime = '';
  savingNurseNoteEdit = false;
  loadingReminderDetails: boolean = false;
  reminderEditForm: { assignedToResourceId: string; dueDate: Date; time: string; reminderOption: string; timezone: string } = {
    assignedToResourceId: 'everyone',
    dueDate: new Date(),
    time: '08:00',
    reminderOption: '15',
    timezone: ''
  };
  resources: { value: string; displayName: string }[] = [];
  readonly assignedToEveryoneOption = { value: 'everyone', displayName: 'Everyone' };
  reminderOptions = [
    { value: '0', label: 'At time of event' },
    { value: '5', label: '5 minutes before' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '1440', label: '1 day before' }
  ];
  timezones = [
    'Asia/Karachi (+0500 UTC+5)',
    'America/New_York (-0500 UTC-5)',
    'America/Los_Angeles (-0800 UTC-8)',
    'Europe/London (+0000 UTC+0)',
    'UTC (+0000 UTC+0)'
  ];

  // Activity notes
  idBeingDeleted: string | null = null;

  // Call recording player: single instance, play/pause
  currentPlayingUrl: string | null = null;
  recordingDurations: Record<string, string> = {};
  private audioElement: HTMLAudioElement | null = null;
  /** Suppresses error toast when pause/stop clears `src` (browser fires `error`). */
  private ignoreRecordingAudioErrors = false;
  waveformHeights: number[] = [6, 8, 10, 14, 16, 18, 20, 20, 18, 14, 12, 10, 10, 12, 14, 12, 10, 8, 6, 4];

  private scrollToBottomRequested = false;
  private userHasScrolledUp = false;
  private readonly scrollBottomThreshold = 80;
  private pinnedHistoryKeys = new Set<string>();
  private static readonly historyPinStoragePrefix = 'engage-history-pins:';
  /** DOS (`yyyy-MM-dd`) keyed by encounter id — filled from loaded activity rows. */
  private encounterDosIsoByEncounterId = new Map<string, string>();

  constructor(
    private readonly _engageActivityService: EngageActivityService,
    private readonly _activityNotesService: ActivityNotesService,
    private readonly _twilioSmsService: TwilioSmsService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _appContextService: ApplicationContextService,
    private readonly _toastService: ToastrService,
    private readonly _eventService: EventService,
    private readonly _remindersService: RemindersService,
    private readonly _modalService: BsModalService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _engageSocketService: EngageSocketService,
    private readonly _admissionService: AdmissionsService
  ) {
    this._appContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.loggedInUserName = `${context.currentlyLoggedInUser.firstName} ${context.currentlyLoggedInUser.lastName}`;
        this.loggedInUserId = context.currentlyLoggedInUser?.id ?? '';
        this.resources = (context.resources || []).map((r) => ({
          value: r.id,
          displayName: r.name
        }));
        const map = context.companyUsersMap || {};
        this.companyUsersMap = Object.fromEntries(
          Object.entries(map).map(([id, u]) => [id, { firstName: u.firstName ?? '', lastName: u.lastName ?? '' }])
        );
      });
  }

  ngOnInit(): void {
    // Initial load: `ngOnChanges` runs first when inputs bind; also handles encounter/referral switches.
    this._eventService.on(BroadcastEventTypes.ActivityNoteAdded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((newActivityNote: ActivityNote) => {
        this.upsertActivityNoteTimelineItem(newActivityNote);
        this.mergedItems = this.pruneClinicNotesMirroredOnEngage(this.mergedItems);
        this.mergedItems.sort((a, b) => this.compareTimelineItems(a, b));
        this.syncEngageReferralListActivityFromMerged();
        this.emitTimelineMerged();
        if (this.shouldAutoScrollToBottom() && !this.userHasScrolledUp) {
          this.scrollToBottomRequested = true;
        }
        this._cdr.markForCheck();
      });
    this._eventService.on(BroadcastEventTypes.SmsMessagesRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadAll());
    this._eventService.on(BroadcastEventTypes.EngageFeedRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadAll());

    this._eventService
      .on(BroadcastEventTypes.AppointmentUpdated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.seedEncounterDosFromSidebarContext();
        this._cdr.markForCheck();
        if (this.nurseNotesTimeline) {
          this.loadNurseNotes();
        }
      });

    this._engageSocketService.messageNew
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => this.onEngageMessageNewSocket(msg));
  }

  private isEngageActivityNotification(engage: EngageActivity): boolean {
    const raw = engage as Record<string, unknown>;
    const n = raw['notification'];
    if (n === 1 || n === true || String(n).trim() === '1') {
      return true;
    }
    return String(raw['hasNotification'] ?? '').toLowerCase() === 'true';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes["patientId"] &&
      !changes["referralId"] &&
      !changes["procedureId"] &&
      !changes["encounterId"] &&
      !changes["timelineApiFilters"] &&
      !changes["nurseNotesTimeline"]
    ) {
      return;
    }

    if (this.nurseNotesTimeline) {
      const pid = (this.patientId || "").trim();
      const procId = this.resolvedProcedureId;
      if (!pid || !procId || procId === pid) {
        return;
      }
      this.userHasScrolledUp = false;
      this.loadNurseNotes();
      return;
    }

    const pid = (this.patientId || '').trim();
    const rid = (this.referralId || '').trim();
    if (!pid && !rid) {
      return;
    }
    this.userHasScrolledUp = false;
    this.loadHistoryPins();
    this.loadAll();
  }

  historyItemKey(item: TimelineItem): string {
    const d = (item.data || {}) as Record<string, unknown>;
    const id = String(d['id'] ?? '').trim();
    const when = String(d['createdAt'] ?? d['createdDateTime'] ?? '').trim();
    const kind = String(d['type'] ?? d['activityType'] ?? '').trim();
    return `${item.source}:${id || when}:${kind}`;
  }

  isHistoryPinned(item: TimelineItem): boolean {
    return this.pinnedHistoryKeys.has(this.historyItemKey(item));
  }

  get pinnedHistoryItems(): TimelineItem[] {
    return this.mergedItems.filter(
      (item) => !this.shouldHideTimelineItem(item) && this.isHistoryPinned(item)
    );
  }

  historyPinTitle(item: TimelineItem): string {
    const d = item.data as Record<string, unknown>;
    if (item.source === 'engage') {
      const a = item.data as EngageActivity;
      const typeLabel = this.shouldShowEngageTypeLabel(a)
        ? this.getEngageTypeLabel(a)
        : '';
      const body = this.getEngageActivityCardBody(a);
      if (typeLabel && body) {
        return `${typeLabel}: ${body}`;
      }
      return body || typeLabel || 'Note';
    }
    if (item.source === 'activity') {
      const notes = String(d['notes'] ?? d['activityType'] ?? '').trim();
      return notes || 'Note';
    }
    if (item.source === 'sms') {
      return String(d['body'] ?? 'Message').trim() || 'Message';
    }
    return String(d['type'] ?? 'Event').trim() || 'Event';
  }

  historyPinActor(item: TimelineItem): string {
    const d = item.data as Record<string, unknown>;
    if (item.source === 'engage') {
      const a = item.data as EngageActivity;
      const ext = a as Record<string, unknown>;
      const raw = String(
        a.activityBy ||
          a.name ||
          a.createdBy ||
          ext['created_by'] ||
          ext['activity_by'] ||
          ''
      ).trim();
      return this.resolveTimelineActorDisplay(raw);
    }
    if (item.source === 'sms') {
      return this.getMessageDisplayName(item.data as TwilioMessage);
    }
    if (item.source === 'event') {
      const raw = this.getEventCreatedByDisplay(item.data as EngageEventByReferralItem);
      if (!raw || raw.toLowerCase() === 'unknown') {
        return '';
      }
      return this.resolveTimelineActorDisplay(raw);
    }
    if (item.source === 'activity') {
      return this.resolveTimelineActorDisplay(String(d['activityBy'] ?? ''));
    }
    return this.resolveTimelineActorDisplay(String(d['activityBy'] ?? ''));
  }

  /** Prospect History meta — map emails / user ids to directory display names. */
  resolveTimelineActorDisplay(raw: string | null | undefined): string {
    const decoded = this.decodeDisplayText(String(raw ?? '').trim()) || String(raw ?? '').trim();
    if (!decoded || decoded.toLowerCase() === 'unknown') {
      return '';
    }
    const fromAdmission = this._admissionService.resolveWorklistUserDisplayName(decoded);
    if (fromAdmission) {
      return fromAdmission;
    }
    if (this.looksLikeUuid(decoded)) {
      return this.resolveUserIdToDisplayName(decoded) || '';
    }
    if (decoded.includes('@')) {
      return '';
    }
    return decoded;
  }

  historyPinDateIso(item: TimelineItem): string {
    const fromPayload = extractEngageActivityTimestampIso(item.data as Record<string, unknown>);
    if (fromPayload) {
      return fromPayload;
    }
    const d = item.data as Record<string, unknown>;
    return String(d['createdDateTime'] || d['createdAt'] || d['created_at'] || d['timestamp'] || '').trim();
  }

  /** Prospect History footer — timestamp only for clinic activity rows (DOS is in the tile header). */
  timelineItemFooterDateDisplay(item: TimelineItem): string {
    if (item.source === 'activity') {
      return this.historyPinDateDisplay(item);
    }
    return this.timelineItemDateDisplay(item);
  }

  /** Prospect History / engage / event footer — `Sep 15, 2026, 8:29 PM · DOS mm/dd/yyyy`. */
  timelineItemDateDisplay(item: TimelineItem): string {
    const base = this.historyPinDateDisplay(item);
    if (!base) {
      return '';
    }
    const dos = this.getTimelineItemEncounterDosDisplay(item);
    if (!dos) {
      return base;
    }
    return `${base} · DOS ${dos}`;
  }

  /** Top-of-tile line for PHI activity rows: author and encounter DOS. */
  activityNoteTitleLine(
    note: ActivityNoteDisplay | null | undefined
  ): string {
    if (!note) {
      return '';
    }
    return formatActivityNoteTitleWithDos(
      note.activityBy,
      this.getActivityNoteEncounterDosDisplay(note)
    );
  }

  private getTimelineItemEncounterDosDisplay(item: TimelineItem): string {
    if (item.source === 'activity') {
      return this.getActivityNoteEncounterDosDisplay(
        item.data as ActivityNoteDisplay
      );
    }
    if (item.source === 'engage') {
      return this.getEngageActivityEncounterDosDisplay(
        item.data as EngageActivity
      );
    }
    if (item.source === 'event') {
      return this.getEventEncounterDosDisplay(
        item.data as EngageEventByReferralItem
      );
    }
    return '';
  }

  getEngageActivityEncounterDosDisplay(
    activity: EngageActivity | Record<string, unknown> | null | undefined
  ): string {
    if (!activity || typeof activity !== 'object') {
      return '';
    }
    const rec = activity as Record<string, unknown>;
    const fromSelected = formatActivityEncounterDosDisplay(
      String(rec['selectedDate'] ?? rec['selected_date'] ?? '')
    );
    if (fromSelected) {
      return fromSelected;
    }
    const eid = String(rec['encounterId'] ?? rec['encounter_id'] ?? '').trim();
    if (eid) {
      const fromLookup = this.encounterDosIsoByEncounterId.get(eid);
      if (fromLookup) {
        return formatActivityEncounterDosDisplay(fromLookup);
      }
      return formatActivityEncounterDosDisplay(
        resolveActivityEncounterDosIso(eid, this.activityEncounterSourceFromCache())
      );
    }
    const appt = extractEngageActivityAppointmentDateDisplay(rec);
    if (!appt) {
      return '';
    }
    const iso = normalizeActivityEncounterDosIso(appt);
    return formatActivityEncounterDosDisplay(iso || appt);
  }

  getEventEncounterDosDisplay(
    event: EngageEventByReferralItem | null | undefined
  ): string {
    const row = this.getEventRowRecord(event);
    if (!row) {
      return '';
    }
    const dosRaw =
      row['dos'] ??
      row['selectedDate'] ??
      row['selected_date'] ??
      row['scheduleDate'] ??
      row['schedule_date'] ??
      row['appointmentDate'] ??
      row['appointment_date'];
    const fromField = formatActivityEncounterDosDisplay(String(dosRaw ?? ''));
    if (fromField) {
      return fromField;
    }
    const eid = String(
      row['encounterId'] ?? row['encounter_id'] ?? event?.encounterId ?? ''
    ).trim();
    if (!eid) {
      return '';
    }
    const fromLookup = this.encounterDosIsoByEncounterId.get(eid);
    if (fromLookup) {
      return formatActivityEncounterDosDisplay(fromLookup);
    }
    return formatActivityEncounterDosDisplay(
      resolveActivityEncounterDosIso(eid, this.activityEncounterSourceFromCache())
    );
  }

  private rememberEncounterDosFromEngagePayload(
    activity: EngageActivity | Record<string, unknown>
  ): void {
    const rec = activity as Record<string, unknown>;
    const eid = String(rec['encounterId'] ?? rec['encounter_id'] ?? '').trim();
    const dos = normalizeActivityEncounterDosIso(
      String(rec['selectedDate'] ?? rec['selected_date'] ?? '')
    );
    if (eid && dos) {
      this.encounterDosIsoByEncounterId.set(eid, dos);
    }
  }

  timelineItemActorDisplay(item: TimelineItem): string {
    return this.historyPinActor(item);
  }

  /** Focus meta line — History uses {@link timelineItemDateDisplay}; empty `createdAt` must not hide `createdDateTime`. */
  historyPinDateDisplay(item: TimelineItem): string {
    const raw = this.historyPinDateIso(item);
    if (!raw) {
      return '';
    }
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return formatEngageActivityCreatedDateTime(item.data as Record<string, unknown>) || raw;
  }

  toggleHistoryPin(item: TimelineItem): void {
    const key = this.historyItemKey(item);
    if (this.pinnedHistoryKeys.has(key)) {
      this.pinnedHistoryKeys.delete(key);
    } else {
      this.pinnedHistoryKeys.add(key);
    }
    this.persistHistoryPins();
    this.historyPinsChanged.emit();
    this._cdr.markForCheck();
  }

  private historyPinStorageKey(): string {
    const rid = (this.referralId || '').trim();
    const pid = (this.patientId || '').trim();
    return `${UnifiedActivityFeedComponent.historyPinStoragePrefix}${rid || pid || 'anon'}`;
  }

  private loadHistoryPins(): void {
    this.pinnedHistoryKeys = new Set<string>();
    try {
      const raw = localStorage.getItem(this.historyPinStorageKey());
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      if (Array.isArray(parsed)) {
        this.pinnedHistoryKeys = new Set(parsed.map((v) => String(v)));
      }
    } catch {
      this.pinnedHistoryKeys = new Set<string>();
    }
  }

  private persistHistoryPins(): void {
    try {
      localStorage.setItem(
        this.historyPinStorageKey(),
        JSON.stringify([...this.pinnedHistoryKeys])
      );
    } catch {
      /* ignore quota / private mode */
    }
  }

  ngOnDestroy(): void {
    this.stopRecording();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (!this.shouldAutoScrollToBottom() || !this.scrollToBottomRequested || !this.scrollContainer) {
      return;
    }
    this.scrollToBottomRequested = false;
    this.scrollToBottom();
    setTimeout(() => this.scrollToBottom(), 100);
    setTimeout(() => this.scrollToBottom(), 300);
  }

  /** Chat/SMS feeds auto-scroll; prospect History stays put (scrollIntoView breaks page layout). */
  private shouldAutoScrollToBottom(): boolean {
    return !this.fullWidthTimeline;
  }

  /** Resolved procedure record id for activity APIs (never patient or encounter id). */
  get resolvedProcedureId(): string {
    const inputId = (this.procedureId || '').trim();
    if (inputId) {
      return inputId;
    }
    const cache = this._applicationCacheService.applicationCache;
    const listingId = (cache?.procedureListing?.id || '').trim();
    if (listingId) {
      return listingId;
    }
    const procId = (cache?.procedure?.id || '').trim();
    if (procId) {
      return procId;
    }
    const encId = (this.encounterId || '').trim();
    const appt = cache?.appointment;
    if (encId && appt?.id === encId) {
      return (appt?.encounterProcedure?.id || '').trim();
    }
    return (cache?.appointment?.encounterProcedure?.id || '').trim();
  }

  private get resolvedPatientIdForNurseNotes(): string {
    const inputId = (this.patientId || '').trim();
    if (inputId) {
      return inputId;
    }
    const cache = this._applicationCacheService.applicationCache;
    return (
      cache?.procedureListing?.patientId ||
      cache?.appointment?.patientId ||
      cache?.appointment?.encountersPatient?.id ||
      cache?.patient?.id ||
      ''
    ).trim();
  }

  /** Activity GET `filter` query is active — timeline shows Engage activity API only (no Twilio/SMS merge). */
  private hasEngageTimelineApiFilters(): boolean {
    return (this.timelineApiFilters ?? []).some((x) => String(x ?? "").trim());
  }

  get filteredItems(): TimelineItem[] {
    const f = this.activityFilter;
    const serverFiltered = this.hasEngageTimelineApiFilters();
    let items: TimelineItem[];
    if (serverFiltered) {
      if (!f?.dateFrom && !f?.dateTo && !f?.dos) {
        items = this.mergedItems;
      } else {
        items = this.mergedItems.filter((item) => this.itemMatchesFilter(item, f));
      }
    } else if (
      !f ||
      (f.categories?.length === 0 &&
        !f.dateFrom &&
        !f.dateTo &&
        !f.dos)
    ) {
      items = this.mergedItems;
    } else {
      items = this.mergedItems.filter((item) => this.itemMatchesFilter(item, f));
    }
    return items.filter((item) => !this.shouldHideTimelineItem(item));
  }

  /** Rows the feed would render with no category filter applied. */
  get visibleItemCount(): number {
    return this.mergedItems.filter((item) => !this.shouldHideTimelineItem(item)).length;
  }

  /** Rows matching a single category — drives the History filter chip counts. */
  countByCategory(category: ActivityFilterCategory): number {
    return this.mergedItems.filter(
      (item) =>
        !this.shouldHideTimelineItem(item) && this.itemMatchesAnyCategory(item, [category])
    ).length;
  }

  /** Omit Engage rows whose type is `added_a_comment` (and variants). Clinic PHI activities keep that type. */
  private shouldHideTimelineItem(item: TimelineItem): boolean {
    if (item.source === 'engage') {
      return isEngageAddedCommentType((item.data as EngageActivity).type);
    }
    return false;
  }

  private itemMatchesFilter(item: TimelineItem, f: ActivityFilter): boolean {
    if (f.categories?.length > 0) {
      const matchesCategory = this.itemMatchesAnyCategory(item, f.categories);
      if (!matchesCategory) return false;
    }
    if (f.dateFrom || f.dateTo) {
      const itemDate = item.sortDate;
      const fromMs = f.dateFrom ? new Date(f.dateFrom).setHours(0, 0, 0, 0) : 0;
      const toMs = f.dateTo ? new Date(f.dateTo).setHours(23, 59, 59, 999) : Number.MAX_SAFE_INTEGER;
      if (itemDate < fromMs || itemDate > toMs) return false;
    }
    if (f.dos) {
      const dosIso = this.timelineItemEncounterDosIso(item);
      if (!dosIso) {
        return false;
      }
      const filterIso = this.activityFilterDosToIso(f.dos);
      if (!filterIso || dosIso !== filterIso) {
        return false;
      }
    }
    return true;
  }

  private activityFilterDosToIso(value: Date | string): string {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) {
      return "";
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  /** Encounter DOS as `yyyy-MM-dd` for filtering (not display). */
  private timelineItemEncounterDosIso(item: TimelineItem): string {
    if (item.source === 'activity') {
      const note = item.data as ActivityNoteDisplay;
      const direct = normalizeActivityEncounterDosIso(note.selectedDate);
      if (direct) {
        return direct;
      }
      const eid = String(note.encounterId ?? '').trim();
      if (eid) {
        const cached = this.encounterDosIsoByEncounterId.get(eid);
        if (cached) {
          return cached;
        }
      }
      return normalizeActivityEncounterDosIso(
        this.getActivityNoteEncounterDosDisplay(note)
      );
    }
    if (item.source === 'engage') {
      const rec = item.data as Record<string, unknown>;
      const direct = normalizeActivityEncounterDosIso(
        String(rec['selectedDate'] ?? rec['selected_date'] ?? '')
      );
      if (direct) {
        return direct;
      }
      const eid = String(
        rec['encounterId'] ?? rec['encounter_id'] ?? rec['associated_id'] ?? ''
      ).trim();
      if (eid) {
        const cached = this.encounterDosIsoByEncounterId.get(eid);
        if (cached) {
          return cached;
        }
      }
      const appt = extractEngageActivityAppointmentDateDisplay(rec);
      return normalizeActivityEncounterDosIso(appt);
    }
    if (item.source === 'event') {
      const row = this.getEventRowRecord(item.data as EngageEventByReferralItem);
      if (!row) {
        return '';
      }
      const raw =
        row['dos'] ??
        row['selectedDate'] ??
        row['selected_date'] ??
        row['scheduleDate'] ??
        row['schedule_date'] ??
        row['appointmentDate'] ??
        row['appointment_date'];
      const direct = normalizeActivityEncounterDosIso(String(raw ?? ''));
      if (direct) {
        return direct;
      }
      const eid = String(
        row['encounterId'] ?? row['encounter_id'] ?? ''
      ).trim();
      if (eid) {
        return this.encounterDosIsoByEncounterId.get(eid) ?? '';
      }
    }
    return '';
  }

  /** Normalize Engage activity `type` / `activityType` (API uses both). */
  resolveEngageActivityTypeToken(a: EngageActivity | Record<string, unknown> | null | undefined): string {
    if (!a || typeof a !== 'object') {
      return '';
    }
    const ext = a as Record<string, unknown>;
    const raw = String(
      ext['activityType'] ?? ext['activity_type'] ?? ext['type'] ?? ''
    )
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return raw;
  }

  isEngageSmsActivity(a: EngageActivity | Record<string, unknown> | null | undefined): boolean {
    return this.resolveEngageActivityTypeToken(a) === 'sms';
  }

  /** Category keys; supports legacy values from older sessions (received / sent). `notes` = clinic internal activity notes (`source === 'activity'`) plus Engage note rows. `activity` = Engage rows that are not note, call or reminder. */
  private itemMatchesAnyCategory(item: TimelineItem, categories: readonly string[]): boolean {
    if (item.source === 'event') {
      return categories.some((c) => c === 'events');
    }
    if (item.source === 'engage') {
      const engage = item.data as EngageActivity;
      const notifOn = this.isEngageActivityNotification(engage);
      const engageType = this.resolveEngageActivityTypeToken(engage);
      const isEngageNote = engageType === 'note' || engageType === 'memo';
      const isEngageSms = engageType === 'sms';
      const isJoin = this.isEngageJoinConversationRow(engage);
      return categories.some((c) => {
        if (c === 'notifications') {
          if (!notifOn) {
            return false;
          }
          if (engageType === 'call') {
            return true;
          }
          return (
            engageType === 'voicemail' ||
            engageType === 'form_filled' ||
            engageType === 'formfilled'
          );
        }
        if (c === 'calls' && engageType === 'call') {
          return true;
        }
        if (c === 'reminders' && engageType === 'reminder') {
          return true;
        }
        if (c === 'messages') {
          return isEngageSms && !isJoin;
        }
        if (c === 'notes' && isEngageNote) {
          return true;
        }
        if (
          c === 'activity' &&
          !isEngageNote &&
          !isEngageSms &&
          !isJoin &&
          engageType !== 'call' &&
          engageType !== 'reminder'
        ) {
          return true;
        }
        return false;
      });
    }
    if (item.source === 'activity') {
      const note = item.data as ActivityNoteDisplay;
      const activityType = String(note.activityType ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
      const isSmsNote = activityType === 'sms';
      return categories.some((c) => {
        if (c === 'messages' && isSmsNote) {
          return true;
        }
        if (isSmsNote) {
          return false;
        }
        return (
          c === 'notes' ||
          (c === 'received' && note.activityBy !== this.loggedInUserName) ||
          (c === 'sent' && note.activityBy === this.loggedInUserName)
        );
      });
    }
    if (item.source === 'sms') {
      const msg = item.data as TwilioMessage;
      const isSent = this.isSentMessage(msg);
      const isJoin = this.isJoinConversationTwilioMessage(msg);
      const receivedMatch = !isSent || isJoin;
      const sentMatch = isSent && !isJoin;
      const isCallSmsRow = !!msg.callType;
      /** SMS chat bubbles only; rows with callType are voicemails / call logs and match `calls`, not `messages`. */
      const isSmsMessageOnly = (receivedMatch || sentMatch) && !isCallSmsRow && !isJoin;
      return categories.some((c) => {
        if (c === 'unread_messages') {
          return isSmsMessageOnly && receivedMatch;
        }
        if (c === 'messages') return isSmsMessageOnly;
        if (c === 'received') return receivedMatch && !isJoin;
        if (c === 'sent') return sentMatch;
        return c === 'calls' && isCallSmsRow;
      });
    }
    return false;
  }

  getStageDisplayName(stageValue: string): string {
    if (!stageValue) return '';
    const opt = STAGE_OPTIONS.find((s) => s.value === stageValue);
    return opt?.display ?? stageValue;
  }

  private getNamesFromMergedItems(items: TimelineItem[]): string[] {
    const names = new Set<string>();
    for (const item of items) {
      if (item.source === 'engage') {
        const by = (item.data as EngageActivity).activityBy ?? (item.data as EngageActivity).name ?? (item.data as EngageActivity).createdBy;
        if (by && by.trim()) names.add(by.trim());
      } else if (item.source === 'activity') {
        const by = (item.data as ActivityNoteDisplay).activityBy;
        if (by && by.trim()) names.add(by.trim());
      } else if (item.source === 'sms') {
        const displayName = this.getMessageDisplayName(item.data as TwilioMessage);
        if (displayName && displayName !== 'Unknown') names.add(displayName.trim());
      } else if (item.source === 'event') {
        const ev = item.data as EngageEventByReferralItem;
        const row = this.getEventRowRecord(ev);
        const by = String(
          row?.['eventCreatedBy'] ?? row?.['event_created_by'] ?? (ev as EngageEventByReferralItem)?.eventCreatedBy ?? ''
        ).trim();
        if (by) names.add(by);
      }
    }
    return Array.from(names).sort();
  }

  getEventCreatedByDisplay(e: EngageEventByReferralItem | null | undefined): string {
    const row = this.getEventRowRecord(e);
    const raw = String(
      row?.['eventCreatedBy'] ??
        row?.['event_created_by'] ??
        row?.['createdBy'] ??
        row?.['created_by'] ??
        (e as EngageEventByReferralItem)?.eventCreatedBy ??
        ''
    ).trim();
    if (raw) {
      const decoded = this.decodeDisplayText(raw) || raw;
      const resolved = this.resolveTimelineActorDisplay(decoded);
      if (resolved) {
        return resolved;
      }
      if (decoded.includes('@')) {
        return '';
      }
      return decoded;
    }
    return 'Unknown';
  }

  /**
   * `eventData[i].eventType` (camelCase or snake_case), or nested `eventData` / `event` on the payload.
   */
  getEventTypeDisplay(e: EngageEventByReferralItem | null | undefined): string {
    const row = this.getEventRowRecord(e);
    const raw = String(
      row?.['eventType'] ??
        row?.['event_type'] ??
        row?.['EventType'] ??
        (e as EngageEventByReferralItem)?.eventType ??
        (e as EngageEventByReferralItem)?.event?.eventType ??
        ''
    ).trim();
    return this.decodeDisplayText(raw) || raw;
  }

  /**
   * `eventData[i].eventDateAndTime` (camelCase or snake_case), or nested `eventData` / `event` on the payload.
   */
  getEventDateAndTimeIso(e: EngageEventByReferralItem | null | undefined): string {
    const row = this.getEventRowRecord(e);
    return String(
      row?.['eventDateAndTime'] ??
        row?.['event_date_and_time'] ??
        row?.['EventDateAndTime'] ??
        (e as EngageEventByReferralItem)?.eventDateAndTime ??
        (e as EngageEventByReferralItem)?.event?.eventDateAndTime ??
        ''
    ).trim();
  }

  /**
   * Resolves GET …/events/by-referral response to the list of row objects (same shape as `eventData[i]` in the API).
   * Supports: `{ eventData: [...] }`, `{ eventData: {…} }`, `{ data: { eventData: … } }`, or a bare array.
   */
  private extractReferralEventsFromApi(raw: unknown): EngageEventByReferralItem[] {
    if (raw == null) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw as EngageEventByReferralItem[];
    }
    if (typeof raw !== 'object') {
      return [];
    }
    const r = raw as Record<string, unknown>;
    const ed = r['eventData'] ?? r['event_data'] ?? r['EventData'];
    if (Array.isArray(ed)) {
      return ed as EngageEventByReferralItem[];
    }
    if (ed != null && typeof ed === 'object' && !Array.isArray(ed)) {
      return [r as EngageEventByReferralItem];
    }
    const inner = r['data'] ?? r['result'] ?? r['body'];
    if (inner != null && typeof inner === 'object') {
      return this.extractReferralEventsFromApi(inner);
    }
    return [];
  }

  private asUnknownRecord(value: unknown): Record<string, unknown> | null {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    return value as Record<string, unknown>;
  }

  private looksLikeEngageEventDataRow(o: Record<string, unknown>): boolean {
    return (
      o['eventType'] != null ||
      o['event_type'] != null ||
      o['EventType'] != null ||
      o['eventDateAndTime'] != null ||
      o['event_date_and_time'] != null ||
      o['EventDateAndTime'] != null ||
      o['eventId'] != null ||
      o['event_id'] != null ||
      o['eventDetails'] != null ||
      o['event_details'] != null ||
      o['LeadStatus'] != null ||
      o['leadStatus'] != null
    );
  }

  /**
   * The object that holds `eventType` / `eventDateAndTime`:
   * - each `eventData[]` element (flat row from API)
   * - or `payload.eventData` when it is a single nested object
   * - or `payload.eventData[0]` when a row incorrectly wraps an array
   * - or legacy `event`
   */
  private getEventRowRecord(e: EngageEventByReferralItem | null | undefined): Record<string, unknown> | null {
    const o = this.asUnknownRecord(e);
    if (!o) {
      return null;
    }
    const nested = o['eventData'] ?? o['event_data'] ?? o['EventData'];
    if (Array.isArray(nested) && nested.length > 0) {
      const first = nested[0];
      const rec = this.asUnknownRecord(first);
      if (rec) {
        return rec;
      }
    }
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
    if (this.looksLikeEngageEventDataRow(o)) {
      return o;
    }
    const ev = o['event'];
    if (ev != null && typeof ev === 'object' && !Array.isArray(ev)) {
      return ev as Record<string, unknown>;
    }
    return o;
  }

  getEventIconSrc(): string {
    return 'assets/images/custom/event.svg';
  }

  /** URL-encoded or HTML-entity text from APIs — show readable strings in the timeline. */
  private decodeDisplayText(value: unknown): string {
    if (value == null) {
      return '';
    }
    let s = String(value).trim();
    if (s === '') {
      return '';
    }
    try {
      if (/%[0-9A-Fa-f]{2}/.test(s)) {
        s = decodeURIComponent(s.replace(/\+/g, ' '));
      }
    } catch {
      /* keep s */
    }
    if (typeof document !== 'undefined') {
      const ta = document.createElement('textarea');
      ta.innerHTML = s;
      s = ta.value;
    }
    return s.trim();
  }

  loadAll(): void {
    if (this.nurseNotesTimeline) {
      this.loadNurseNotes();
      return;
    }

    const pid = (this.patientId || '').trim();
    const rid = (this.referralId || '').trim();
    const effectivePatientId = resolveClinicPatientIdDistinctFromReferral(pid, rid);
    if (!effectivePatientId && !rid) return;

    this.isLoading = true;
    const procId = this.resolvedProcedureId;
    const hasValidProcedureScope = !!procId && procId !== effectivePatientId;

    const cachePatientId = (
      this._applicationCacheService.applicationCache.patient?.id || ''
    ).trim();
    const reminderPatientId =
      effectivePatientId ||
      resolveClinicPatientIdDistinctFromReferral(cachePatientId, rid);
    const timelineFilters = this.timelineApiFilters;
    const engageOnlyTimeline = this.hasEngageTimelineApiFilters();

    if (rid && !effectivePatientId) {
      forkJoin({
        engage: this._engageActivityService.getReferralActivities(rid, timelineFilters, {
          isNurseNote: false,
        }),
        activity: of([]),
        sms: engageOnlyTimeline
          ? of([] as TwilioMessage[])
          : this._twilioSmsService
              .getReferralMessages(rid)
              .pipe(catchError(() => of([] as TwilioMessage[]))),
        reminders: engageOnlyTimeline
          ? of([])
          : reminderPatientId
            ? this._remindersService.getReminders(reminderPatientId).pipe(catchError(() => of([])))
            : of([]),
        events: of({ eventData: [] } as any),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => this.mergeTimelineFromForkJoinResult(res),
          error: () => {
            this.isLoading = false;
            this.mergedItems = [];
            this.chatNamesReady.emit([]);
            this.emitTimelineMerged();
            this._cdr.markForCheck();
          },
        });
      return;
    }

    const activityObs =
      !engageOnlyTimeline && effectivePatientId
        ? this._activityNotesService
            .getPatientActivitiesByPatientId(effectivePatientId, {
              isNurseNote: false,
            })
            .pipe(catchError(() => of([])))
        : of([]);

    const engageObs =
      hasValidProcedureScope && !engageOnlyTimeline
        ? of([] as EngageActivity[])
        : effectivePatientId
          ? this._engageActivityService
              .getPatientActivities(effectivePatientId, timelineFilters, {
                isNurseNote: false,
              })
              .pipe(catchError(() => of([] as EngageActivity[])))
          : this._engageActivityService.getReferralActivities(rid, timelineFilters, {
              isNurseNote: false,
            }).pipe(catchError(() => of([] as EngageActivity[])));

    forkJoin({
      engage: engageObs,
      activity: activityObs,
      sms: engageOnlyTimeline
        ? of([] as TwilioMessage[])
        : effectivePatientId
          ? this._twilioSmsService.getPatientMessages(effectivePatientId).pipe(
              catchError(() => of([] as TwilioMessage[]))
            )
          : this._twilioSmsService.getReferralMessages(rid).pipe(
              catchError(() => of([] as TwilioMessage[]))
            ),
      reminders: engageOnlyTimeline
        ? of([])
        : reminderPatientId
          ? this._remindersService.getReminders(reminderPatientId).pipe(catchError(() => of([])))
          : of([]),
      events: of({ eventData: [] } as any),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => this.mergeTimelineFromForkJoinResult(res),
        error: (err) => {
          this._toastService.error(err?.error?.message || 'Unable to load activity.', 'Error!');
          this.isLoading = false;
          this.mergedItems = [];
          this.chatNamesReady.emit([]);
          this.emitTimelineMerged();
          this._cdr.markForCheck();
        },
      });
  }

  /** Procedure nurse notes: only GET activities with `isNurseNote=true`. */
  private loadNurseNotes(): void {
    this.cancelNurseNoteInlineEdit();
    const pid = this.resolvedPatientIdForNurseNotes;
    const procId = this.resolvedProcedureId;
    if (!pid || !procId || procId === pid) {
      return;
    }

    this.isLoading = true;
    this._activityNotesService
      .getPatientActivitiesByProcedureId(pid, procId, { isNurseNote: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (activityItems) =>
          this.mergeTimelineFromForkJoinResult({
            engage: [],
            activity: activityItems,
            sms: [],
            reminders: [],
            events: { eventData: [] },
          }),
        error: (err) => {
          this._toastService.error(err?.error?.message || 'Unable to load activity.', 'Error!');
          this.isLoading = false;
          this.mergedItems = [];
          this.chatNamesReady.emit([]);
          this.emitTimelineMerged();
          this._cdr.markForCheck();
        },
      });
  }

  private mergeTimelineFromForkJoinResult(res: any): void {
    const procId = this.resolvedProcedureId;
    const engageItems: EngageActivity[] = Array.isArray(res.engage)
      ? res.engage
      : res.engage?.data ?? res.engage?.activities ?? [];
    if (this.hasEngageTimelineApiFilters()) {
      this._eventService.broadcast(BroadcastEventTypes.WorksheetEngageTimelineLoaded, {
        referralId: (this.referralId || '').trim(),
        patientId: (this.patientId || '').trim(),
        engageActivities: engageItems,
      });
    }
    const activityItems: ActivityNote[] = Array.isArray(res.activity)
      ? (res.activity as ActivityNote[])
      : [];
    this.encounterDosIsoByEncounterId = buildEncounterDosLookupFromActivityNotes(
      activityItems,
      { defaultEncounterId: (this.encounterId || "").trim() || undefined }
    );
    for (const raw of engageItems) {
      const rec = raw as Record<string, unknown>;
      const eid = String(
        rec["encounterId"] ?? rec["encounter_id"] ?? rec["associated_id"] ?? ""
      ).trim();
      const dos = normalizeActivityEncounterDosIso(
        String(rec["selectedDate"] ?? rec["selected_date"] ?? "")
      );
      if (eid && dos && !this.encounterDosIsoByEncounterId.has(eid)) {
        this.encounterDosIsoByEncounterId.set(eid, dos);
      }
    }
    this.seedEncounterDosFromSidebarContext();
    const reminders: {
      id: string;
      description: string;
      dueDate?: string;
      time?: string;
      createdBy?: string;
      createdById?: string;
      assignedUserId?: string;
      assignedTo?: string;
      assignedToResourceId?: string;
      [key: string]: unknown;
    }[] = Array.isArray(res.reminders) ? res.reminders : [];
    let smsItems: TwilioMessage[] = [];
    if (Array.isArray(res.sms)) smsItems = res.sms;
    else if (res.sms?.data) smsItems = res.sms.data;
    else if (res.sms?.messages) smsItems = res.sms.messages;

    smsItems = smsItems.map((m) => this.normalizeTwilioMmsFields(m));

    const eventsItems = this.extractReferralEventsFromApi(res?.events);

    this.resolveSmsNames(smsItems);
    for (const m of smsItems) {
      if (m.body) {
        m.body = this.decodeDisplayText(m.body) || m.body;
      }
      if (m.senderName) {
        m.senderName = this.decodeDisplayText(m.senderName) || m.senderName;
      }
    }
    const engageMapped = engageItems.map((a) => {
      const normalized = this.normalizeEngageActivity(a);
      this.rememberEncounterDosFromEngagePayload(normalized);
      const isReminder =
        String(normalized.type || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_') === 'reminder';
      if (isReminder) {
        const rawRec = a as Record<string, unknown>;
        const desc = (normalized.notes ?? normalized.comment ?? '').trim();
        const actDueDate = (normalized as any).dueDate;
        const normDesc = (s: string) =>
          s.trim().toLowerCase().replace(/\s+/g, ' ');
        const existingRemId = String((normalized as any).reminderId ?? '').trim();
        const linkId = String(
          rawRec['reminderId'] ?? rawRec['reminder_id'] ?? rawRec['reminderID'] ?? ''
        ).trim();
        let match = existingRemId
          ? reminders.find((r) => String(r.id).trim() === existingRemId)
          : undefined;
        if (!match && linkId) {
          match = reminders.find((r) => String(r.id).trim() === linkId);
        }
        if (!match) {
          const actId = String(a.id ?? rawRec['id'] ?? '').trim();
          if (actId) {
            match = reminders.find((r) => String(r.id).trim() === actId);
          }
        }
        if (!match) {
          match = reminders.find((r) => {
            const rDesc = (r.description || '').trim();
            if (rDesc !== desc && normDesc(rDesc) !== normDesc(desc)) return false;
            if (actDueDate && r.dueDate)
              return (
                actDueDate === r.dueDate ||
                actDueDate.startsWith?.(r.dueDate) ||
                r.dueDate.startsWith?.(actDueDate)
              );
            return true;
          });
        }
        if (match) {
          (normalized as any).reminderId = match.id;
          const m = match as Record<string, unknown>;
          const assigneeLine = this.resolveReminderAssigneeDisplayFromApiRow(m, true);
          if (assigneeLine) {
            (normalized as EngageActivity).activityBy =
              this.decodeDisplayText(assigneeLine) || assigneeLine;
          } else {
            const createdByName = String(m['createdBy'] ?? m['created_by'] ?? '').trim();
            const cbId = String(
              m['createdById'] ?? m['created_by_id'] ?? m['createdByUserId'] ?? ''
            ).trim();
            const fromUid =
              cbId && this.looksLikeUuid(cbId)
                ? this.resolveUserIdToDisplayName(cbId)
                : null;
            const resolvedName =
              createdByName && createdByName.toLowerCase() !== 'unknown'
                ? createdByName
                : fromUid || '';
            const cur = String((normalized as EngageActivity).activityBy || '').trim();
            if (resolvedName && (!cur || cur.toLowerCase() === 'unknown')) {
              (normalized as EngageActivity).activityBy =
                this.decodeDisplayText(resolvedName) || resolvedName;
            }
          }
        }
        const curBy = String((normalized as EngageActivity).activityBy || '').trim();
        if (!curBy || curBy.toLowerCase() === 'unknown') {
          const fromEngage = this.resolveReminderAssigneeFromEngageActivityOnly(a);
          if (fromEngage) {
            (normalized as EngageActivity).activityBy =
              this.decodeDisplayText(fromEngage) || fromEngage;
          }
        }
        const still = String((normalized as EngageActivity).activityBy || '').trim();
        if (!still || still.toLowerCase() === 'unknown') {
          const postName = String(a.name ?? rawRec['name'] ?? '').trim();
          if (postName && postName.toLowerCase() !== 'unknown') {
            (normalized as EngageActivity).activityBy =
              this.decodeDisplayText(postName) || postName;
          }
        }
        const finalBy = String((normalized as EngageActivity).activityBy || '').trim();
        if (!finalBy || finalBy.toLowerCase() === 'unknown') {
          const reExtract = this.extractEngageActivityAuthorRaw(a);
          if (reExtract && reExtract.toLowerCase() !== 'unknown') {
            (normalized as EngageActivity).activityBy =
              this.decodeDisplayText(reExtract) || reExtract;
          }
        }
      }
      return normalized;
    });
    const activityMapped = activityItems.map((n) =>
      this.mapNoteToDisplayNote(n)
    );

    const items: TimelineItem[] = [];
    for (const a of engageMapped) {
      if (isEngageAddedCommentType(a.type)) {
        continue;
      }
      items.push({ source: 'engage', sortDate: this.getEngageSortDate(a), data: a });
    }
    for (const n of activityMapped) {
      items.push({
        source: 'activity',
        sortDate: new Date(n.createdAt || '').getTime(),
        data: n,
      });
    }
    for (const m of smsItems) {
      items.push({ source: 'sms', sortDate: new Date(m.createdAt || '').getTime(), data: m });
    }
    for (const e of eventsItems) {
      const iso = this.getEventDateAndTimeIso(e);
      const t = iso ? new Date(iso).getTime() : 0;
      items.push({ source: 'event', sortDate: Number.isFinite(t) ? t : 0, data: e });
    }
    items.sort((a, b) => this.compareTimelineItems(a, b));
    this.mergedItems = this.pruneClinicNotesMirroredOnEngage(items);
    this.syncEngageReferralListActivityFromMerged();
    this.chatNamesReady.emit(this.getNamesFromMergedItems(this.mergedItems));
    this.emitTimelineMerged();
    this.isLoading = false;
    if (procId) this._applicationCacheService.setApiStatus(ApiStatus.Success, ReferralApis.Activity);
    if (this.shouldAutoScrollToBottom()) {
      this.scrollToBottomRequested = true;
    }
    this._cdr.markForCheck();
  }

  private getEngageSortDate(a: EngageActivity): number {
    const iso = extractEngageActivityTimestampIso(a as Record<string, unknown>);
    if (iso) {
      const t = new Date(iso).getTime();
      if (!Number.isNaN(t)) {
        return t;
      }
    }
    return 0;
  }

  /** Engage "New Request" card (`form_filled`) — always first in the feed, then everything else by time. */
  private isNewRequestTimelineItem(item: TimelineItem): boolean {
    if (item.source !== 'engage') {
      return false;
    }
    const t = String((item.data as EngageActivity)?.type ?? '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    return t === 'form_filled';
  }

  /**
   * Primary order: all New Request rows first (by their timestamps among themselves), then every other row by time.
   * Tie-breaker: same instant — see {@link timelineSortRank} (lower rank sorts earlier).
   */
  private compareTimelineItems(a: TimelineItem, b: TimelineItem): number {
    const an = this.isNewRequestTimelineItem(a);
    const bn = this.isNewRequestTimelineItem(b);
    if (an && bn) {
      return a.sortDate - b.sortDate;
    }
    if (an) {
      return -1;
    }
    if (bn) {
      return 1;
    }
    const d = a.sortDate - b.sortDate;
    if (d !== 0) {
      return d;
    }
    return this.timelineSortRank(a) - this.timelineSortRank(b);
  }

  /**
   * When multiple items share the same timestamp (and neither is New Request), tie-break order for stable layout.
   */
  private timelineSortRank(item: TimelineItem): number {
    if (item.source === 'engage') {
      return 20;
    }
    if (item.source === 'event') {
      return 10;
    }
    return 30;
  }

  /**
   * Engage referrals list activity-date shows the **earliest** merged event (top of timeline), not the latest.
   */
  private syncEngageReferralListActivityFromMerged(): void {
    const rid = (this.referralId || '').trim();
    if (!rid || !this.mergedItems.length) {
      return;
    }
    let min = Number.POSITIVE_INFINITY;
    for (const it of this.mergedItems) {
      if (Number.isFinite(it.sortDate) && it.sortDate > 0 && it.sortDate < min) {
        min = it.sortDate;
      }
    }
    if (!Number.isFinite(min) || min <= 0) {
      return;
    }
    this._eventService.broadcast(BroadcastEventTypes.EngageReferralFirstActivitySynced, {
      referralId: rid,
      firstActivityMs: min,
    });
  }

  /** Flatten nested reminder / payload blobs on Engage activity rows. */
  private flattenEngageReminderEnrichmentSource(raw: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = { ...raw };
    const nested =
      raw['reminder'] ??
      raw['Reminder'] ??
      raw['reminderData'] ??
      raw['reminder_data'] ??
      raw['payload'];
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
      Object.assign(out, nested as Record<string, unknown>);
    }
    const inner = raw['data'] ?? raw['body'] ?? raw['result'];
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
      Object.assign(out, inner as Record<string, unknown>);
    }
    return out;
  }

  /**
   * Resolve author / assignee ids from Engage activity (esp. reminders) before defaulting to Unknown.
   */
  private extractEngageActivityAuthorRaw(a: EngageActivity): string {
    const raw = a as Record<string, unknown>;
    const t = String(a.type || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    const ext =
      t === 'reminder'
        ? this.flattenEngageReminderEnrichmentSource(raw)
        : raw;
    const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
    const candidates = [
      str(a.name),
      str(a.activityBy),
      str(a.createdBy),
      str(ext['created_by']),
      str(ext['activity_by']),
      str(ext['createdByName']),
      str(ext['created_by_name']),
      str(ext['userName']),
      str(ext['user_name']),
      str(ext['authorName']),
      str(ext['author_name']),
      str(ext['author']),
    ];
    for (const s of candidates) {
      if (s && s.toLowerCase() !== 'unknown') {
        const resolved = this._admissionService.resolveWorklistUserDisplayName(s);
        if (resolved) {
          return resolved;
        }
        if (!s.includes('@') && !this.looksLikeUuid(s)) {
          return s;
        }
        continue;
      }
    }
    const uidKeys = [
      'assignedUserId',
      'assigned_user_id',
      'assigneeUserId',
      'assignee_user_id',
      'createdByUserId',
      'created_by_user_id',
      'userId',
      'user_id',
      'createdById',
      'created_by_id',
    ] as const;
    for (const k of uidKeys) {
      const s = str(ext[k]);
      if (s && this.looksLikeUuid(s)) {
        const resolved = this.resolveUserIdToDisplayName(s);
        if (resolved) {
          return resolved;
        }
      }
    }
    const createdByMaybeId = str(a.createdBy);
    if (createdByMaybeId && this.looksLikeUuid(createdByMaybeId)) {
      const resolved = this.resolveUserIdToDisplayName(createdByMaybeId);
      if (resolved) {
        return resolved;
      }
    }
    return '';
  }

  private normalizeEngageActivity(a: EngageActivity): EngageActivity {
    let byRaw = this.extractEngageActivityAuthorRaw(a);
    const t = String(a.type || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    /** Inbound / voicemail / missed — API often omits name; use context patient, never "Unknown". */
    if (
      t === 'voicemail' ||
      t === 'missed' ||
      t === 'missed_call' ||
      t === 'inbound'
    ) {
      if (!byRaw || byRaw.toLowerCase() === 'unknown') {
        const cached = this.getCachedContextPatientDisplayName();
        byRaw = cached || 'Patient';
      }
    } else if (!byRaw) {
      /** Reminder: defer to merge + {@link getReminderUnifiedTitle}; avoid literal Unknown in feed. */
      byRaw = t === 'reminder' ? '' : 'Unknown';
    }
    const byDecoded = (this.decodeDisplayText(byRaw) || byRaw || '').trim();
    const by =
      this.resolveTimelineActorDisplay(byDecoded) ||
      (byDecoded.includes('@') ? '' : byDecoded) ||
      (t === 'reminder' ? '' : 'Unknown');
    const textRaw = a.comment ?? a.notes ?? '';
    const text = this.decodeDisplayText(textRaw) || textRaw;
    const recordingUrl = this.resolveRecordingUrlFromPayload(a);
    const iso = extractEngageActivityTimestampIso(a as Record<string, unknown>);
    return {
      ...a,
      activityBy: by,
      notes: text,
      createdAt: iso ?? a.createdAt ?? a.created_at ?? '',
      createdDateTime: iso ?? a.createdDateTime ?? a.createdAt ?? a.created_at ?? '',
      ...(recordingUrl ? { recordingUrl } : {})
    };
  }

  /** API may send camelCase, snake_case, or nested recording links. */
  private resolveRecordingUrlFromPayload(data: EngageActivity | TwilioMessage | Record<string, unknown>): string {
    const o = data as Record<string, unknown>;
    const keys = [
      'recordingUrl',
      'recording_url',
      'recordingURL',
      'RecordingUrl',
      'mediaUrl',
      'media_url',
      'twilioRecordingUrl',
      'twilio_recording_url'
    ];
    for (const k of keys) {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
    }
    return '';
  }

  /** Align with wa-engage-v2 `ReferralMessage` / API snake_case. */
  private normalizeTwilioMmsFields(raw: any): TwilioMessage {
    const m = { ...raw } as TwilioMessage;
    if (!m.mediaUrl && raw.media_url) {
      m.mediaUrl = raw.media_url;
    }
    if (!m.mediaContentType && raw.media_content_type) {
      m.mediaContentType = raw.media_content_type;
    }
    const fileName =
      m.mediaFileName ||
      raw.mediaFileName ||
      raw.media_filename ||
      m.mediaFilename ||
      raw.mediaFilename;
    if (fileName) {
      m.mediaFileName = fileName;
      m.mediaFilename = fileName;
    }
    if (m.patientId == null || m.patientId === undefined) {
      m.patientId = raw.patientId != null ? String(raw.patientId) : '';
    }
    return m;
  }

  /** Public URL for MMS — same as Engage message list. */
  getSmsMediaUrl(m: TwilioMessage): string {
    const u = m.mediaUrl || (m as any).media_url;
    return u && String(u).trim() ? String(u).trim() : '';
  }

  getSmsAttachmentDisplayName(m: TwilioMessage): string {
    return (
      m.mediaFileName ||
      m.mediaFilename ||
      (m as any).media_filename ||
      'attachment'
    );
  }

  isSmsImageFile(m: TwilioMessage): boolean {
    const ct = m.mediaContentType || (m as any).media_content_type;
    if (typeof ct === 'string' && ct.startsWith('image/')) {
      return true;
    }
    const imageExt = /\.(jpe?g|png|gif|webp|bmp|heic|heif)(\?|$)/i;
    const url = this.getSmsMediaUrl(m);
    const name = this.getSmsAttachmentDisplayName(m);
    return imageExt.test(url) || imageExt.test(name);
  }

  /** Plain SMS/MMS chat bubble (not call, voicemail, or join). */
  isPlainSmsChatMessage(m: TwilioMessage): boolean {
    if (this.isJoinConversationTwilioMessage(m)) {
      return false;
    }
    return !String(m.callType || '').trim();
  }

  /** Hide generic MMS placeholder when image thumbnail is shown. */
  shouldShowSmsMessageBody(m: TwilioMessage): boolean {
    const body = String(m.body || '').trim();
    if (!body) {
      return false;
    }
    if (this.isSmsImageFile(m) && body.toLowerCase() === 'mms') {
      return false;
    }
    return true;
  }

  hasSmsImageAttachment(m: TwilioMessage): boolean {
    return this.isSmsImageFile(m) && !!this.getSmsMediaUrl(m);
  }

  /** Image MMS with no visible text — thumbnail only, no bubble background. */
  isSmsMmsImageOnly(m: TwilioMessage): boolean {
    return (
      this.isPlainSmsChatMessage(m) &&
      this.hasSmsImageAttachment(m) &&
      !this.shouldShowSmsMessageBody(m)
    );
  }

  /** Text/call bubble (excludes standalone image MMS). */
  hasSmsTextBubble(m: TwilioMessage): boolean {
    if (!this.isPlainSmsChatMessage(m)) {
      return true;
    }
    if (this.isSmsMmsImageOnly(m)) {
      return false;
    }
    return (
      this.shouldShowSmsMessageBody(m) ||
      this.isOutboundCall(m) ||
      this.showCallRecordingPlayer(m) ||
      (!!this.getSmsMediaUrl(m) && !this.isSmsImageFile(m)) ||
      (!this.getSmsMediaUrl(m) && !!m.mediaFilename)
    );
  }

  smsMediaFileIcon(m: TwilioMessage): string {
    const ct = m.mediaContentType || (m as any).media_content_type;
    if (!ct) {
      return 'fa-file';
    }
    if (String(ct).startsWith('image/')) {
      return 'fa-image';
    }
    if (ct === 'application/pdf') {
      return 'fa-file-pdf';
    }
    return 'fa-file';
  }

  onSmsMediaClick(event: Event, m: TwilioMessage): void {
    event.preventDefault();
    const url = this.getSmsMediaUrl(m);
    if (!url) {
      return;
    }
    this._modalService.show(MediaPreviewModalComponent, {
      class: 'modal-dialog-centered modal-lg',
      initialState: {
        mediaUrl: url,
        mediaContentType: m.mediaContentType || (m as any).media_content_type || 'application/octet-stream',
        mediaFileName: this.getSmsAttachmentDisplayName(m),
      },
    });
  }

  private resolveSmsNames(messages: TwilioMessage[]): void {
    messages.filter((m) => this.isJoinConversationTwilioMessage(m) && m.userId).forEach((m) => {
      const user = m.userId ? this.companyUsersMap[m.userId] : null;
      if (user && (user.firstName || user.lastName)) {
        m.firstName = user.firstName;
        m.lastName = user.lastName;
      }
    });
  }

  private mapNoteToDisplayNote(note: ActivityNote): ActivityNoteDisplay {
    const by = this.decodeDisplayText(note.activityBy || '') || note.activityBy || '';
    const notes =
      this.decodeDisplayText(note.notes || note.comment || '') ||
      note.notes ||
      note.comment ||
      '';
    const rec = note as ActivityNote & Record<string, unknown>;
    const iso = extractEngageActivityTimestampIso(rec);
    return {
      id: note.id,
      activityBy: by,
      activityType: String(rec.activityType ?? rec['activity_type'] ?? '').trim(),
      createdAt: iso ?? note.createdAt ?? '',
      notes,
      patientId: this.patientId,
      procedureId: this.resolvedProcedureId,
      encounterId:
        String(note.encounterId ?? '').trim() ||
        String(this.encounterId ?? '').trim() ||
        undefined,
      selectedDate: String(note.selectedDate ?? '').trim() || undefined,
      userInitials: by ? Utils.getInitialsFromFullName(by) : '',
      isNurseNote: note.isNurseNote === true,
    };
  }

  getActivityNoteEncounterDosDisplay(
    note: ActivityNoteDisplay | null | undefined
  ): string {
    if (!note) {
      return '';
    }
    // Appointment cache often hydrates after the first activity paint (referral-details).
    this.seedEncounterDosFromSidebarContext();
    const sidebarEncounterId = (this.encounterId || '').trim() || undefined;
    const noteForDos: ActivityNoteDisplay = {
      ...note,
      encounterId:
        String(note.encounterId ?? '').trim() ||
        sidebarEncounterId ||
        note.encounterId,
    };
    return resolveActivityNoteEncounterDosDisplay(
      noteForDos,
      this.activityEncounterSourceFromCache(),
      this.encounterDosIsoByEncounterId,
      sidebarEncounterId
    );
  }

  /** When activity rows omit `selectedDate`, use the open encounter's DOS for the whole feed. */
  private seedEncounterDosFromSidebarContext(): void {
    const eid = (this.encounterId || '').trim();
    if (!eid || this.encounterDosIsoByEncounterId.has(eid)) {
      return;
    }
    const dos = resolveActivityEncounterDosIso(
      eid,
      this.activityEncounterSourceFromCache()
    );
    if (dos) {
      this.encounterDosIsoByEncounterId.set(eid, dos);
    }
  }

  private activityEncounterSourceFromCache(): ActivityEncounterSource {
    const cache = this._applicationCacheService.applicationCache;
    return {
      procedureListing: cache?.procedureListing,
      appointment: cache?.appointment,
      procedure: cache?.procedure,
    };
  }

  private rememberEncounterDosFromNote(note: ActivityNote | null | undefined): void {
    const eid = String(note?.encounterId || '').trim();
    const dos = String(note?.selectedDate || '').trim().split('T')[0];
    if (eid && dos && !this.encounterDosIsoByEncounterId.has(eid)) {
      this.encounterDosIsoByEncounterId.set(eid, dos);
    }
  }

  private upsertActivityNoteTimelineItem(note: ActivityNote): void {
    this.seedEncounterDosFromSidebarContext();
    this.rememberEncounterDosFromNote(note);
    const display = this.mapNoteToDisplayNote(note);
    const id = String(display.id || '').trim();
    const entry: TimelineItem = {
      source: 'activity',
      sortDate: new Date(display.createdAt || '').getTime(),
      data: display,
    };

    if (id) {
      const existingIndex = this.mergedItems.findIndex(
        (item) =>
          item.source === 'activity' &&
          String((item.data as ActivityNoteDisplay).id || '') === id
      );
      if (existingIndex >= 0) {
        this.mergedItems[existingIndex] = entry;
      } else {
        this.mergedItems.push(entry);
      }
    } else {
      this.mergedItems.push(entry);
    }

    this.pruneRedundantScheduleCreatedTimelineItems(note);
  }

  /** Remove timestamp-only Schedule Created stubs once a detailed row exists. */
  private pruneRedundantScheduleCreatedTimelineItems(
    latestNote?: ActivityNote
  ): void {
    const activityNotes: ActivityNote[] = this.mergedItems
      .filter((item) => item.source === 'activity')
      .map((item) => {
        const display = item.data as ActivityNoteDisplay;
        return {
          id: display.id,
          activityType: display.activityType,
          notes: display.notes,
          encounterId: this.encounterId,
          createdAt: display.createdAt,
        } as ActivityNote;
      });
    if (latestNote?.id) {
      const idx = activityNotes.findIndex((n) => n.id === latestNote.id);
      if (idx >= 0) {
        activityNotes[idx] = latestNote;
      } else {
        activityNotes.push(latestNote);
      }
    }

    const encounterIdsWithDetailed = new Set<string>();
    for (const activityNote of activityNotes) {
      if (isEmptyScheduleCreatedActivity(activityNote)) {
        continue;
      }
      const type = String(activityNote.activityType || '').trim();
      if (type !== 'Schedule Created') {
        continue;
      }
      const eid = String(activityNote.encounterId || '').trim();
      if (eid && activityNoteBody(activityNote)) {
        encounterIdsWithDetailed.add(eid);
      }
    }
    if (!encounterIdsWithDetailed.size) {
      return;
    }

    this.mergedItems = this.mergedItems.filter((item) => {
      if (item.source !== 'activity') {
        return true;
      }
      const display = item.data as ActivityNoteDisplay;
      const type = String(display.activityType || '').trim();
      if (type !== 'Schedule Created') {
        return true;
      }
      const eid = String(this.encounterId || '').trim();
      if (!eid || !encounterIdsWithDetailed.has(eid)) {
        return true;
      }
      return !isEmptyScheduleCreatedActivity(
        { activityType: display.activityType, notes: display.notes, encounterId: eid },
        eid
      );
    });
  }

  /**
   * Engage `type: note` rows and clinic PHI `Manual` notes are often the same save
   * ({@link AddActivityNoteComponent} mirrors to both APIs). Keep the Engage row only.
   */
  private pruneClinicNotesMirroredOnEngage(items: TimelineItem[]): TimelineItem[] {
    const engageNoteKeys = items
      .filter(
        (it) =>
          it.source === 'engage' && this.isEngageNoteTimelineActivity(it.data as EngageActivity)
      )
      .map((it) => ({
        body: this.timelineNoteBodyFromEngage(it.data as EngageActivity),
        sortDate: it.sortDate,
      }))
      .filter((k) => !!k.body);

    if (!engageNoteKeys.length) {
      return items;
    }

    const mirrorWindowMs = 5 * 60 * 1000;

    return items.filter((it) => {
      if (it.source !== 'activity') {
        return true;
      }
      const note = it.data as ActivityNoteDisplay;
      if (!this.isClinicNoteMirroredOnEngageCandidate(note)) {
        return true;
      }
      const body = this.timelineNoteBodyFromClinic(note);
      if (!body) {
        return true;
      }
      const isDuplicate = engageNoteKeys.some((k) => {
        if (k.body !== body) {
          return false;
        }
        if (!k.sortDate || !it.sortDate) {
          return true;
        }
        return Math.abs(it.sortDate - k.sortDate) <= mirrorWindowMs;
      });
      return !isDuplicate;
    });
  }

  private isEngageNoteTimelineActivity(a: EngageActivity): boolean {
    const t = String(a.type ?? '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    return t === 'note' || t === 'memo';
  }

  private isClinicNoteMirroredOnEngageCandidate(note: ActivityNoteDisplay): boolean {
    if (note.isNurseNote) {
      return false;
    }
    if (this.isReferralCreatedNote(note)) {
      return false;
    }
    const type = String(note.activityType ?? '').trim();
    if (type === 'Schedule Created') {
      return false;
    }
    const normalized = type.toLowerCase().replace(/[\s_-]+/g, '_');
    return !normalized || normalized === 'manual' || normalized === 'memo' || normalized === 'note';
  }

  private normalizeTimelineNoteBody(raw: string): string {
    return this.stripProspectSideHtml(this.decodeDisplayText(raw) || raw)
      .trim()
      .toLowerCase();
  }

  private timelineNoteBodyFromEngage(a: EngageActivity): string {
    return this.normalizeTimelineNoteBody(String(a.notes ?? a.comment ?? ''));
  }

  private timelineNoteBodyFromClinic(note: ActivityNoteDisplay): string {
    return this.normalizeTimelineNoteBody(String(note.notes ?? ''));
  }

  onScroll(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const atBottom = el.scrollHeight - el.clientHeight - el.scrollTop <= this.scrollBottomThreshold;
    this.userHasScrolledUp = !atBottom;
  }

  private scrollToBottom(): void {
    if (!this.shouldAutoScrollToBottom()) {
      return;
    }
    const anchor = this.scrollAnchor?.nativeElement;
    if (anchor) {
      anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
      this.userHasScrolledUp = false;
      return;
    }
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const target = Math.max(0, el.scrollHeight - el.clientHeight);
    el.scrollTop = target;
    if (Math.abs(el.scrollTop - target) < 2) this.userHasScrolledUp = false;
  }

  isReferralCreatedNote(note: { activityType?: string; notes?: string }): boolean {
    if (isReferralCreatedActivityType(note?.activityType)) {
      return true;
    }
    const notes = (note?.notes ?? '').toLowerCase();
    return notes.includes('referral created');
  }

  getNurseNoteInitials(note: ActivityNoteDisplay): string {
    const fromNote = (note?.userInitials ?? '').trim();
    if (fromNote) {
      return fromNote;
    }
    const by = (note?.activityBy ?? '').trim();
    return by ? Utils.getInitialsFromFullName(by) : '?';
  }

  getNurseNoteBodyText(note: ActivityNoteDisplay): string {
    return (
      this.decodeDisplayText(note?.notes || '') ||
      note?.notes ||
      this.decodeDisplayText(note?.activityType || '') ||
      note?.activityType ||
      ''
    );
  }

  isNurseNoteStatusStyle(note: ActivityNoteDisplay): boolean {
    if (this.isReferralCreatedNote(note)) {
      return true;
    }
    const type = (note?.activityType ?? '').trim();
    if (!type) {
      return false;
    }
    const notes = (note?.notes ?? '').trim();
    if (notes && notes !== type) {
      return false;
    }
    return true;
  }

  getNurseNoteEventAuthor(ev: EngageEventByReferralItem): string {
    const raw =
      (ev as any)?.eventCreatedBy ??
      (ev as any)?.event_created_by ??
      '';
    return this.decodeDisplayText(String(raw)) || String(raw).trim() || 'System';
  }

  getNurseNoteEventInitials(ev: EngageEventByReferralItem): string {
    const author = this.getNurseNoteEventAuthor(ev);
    return author && author !== 'System' ? Utils.getInitialsFromFullName(author) : '—';
  }

  /**
   * Edit/Delete (⋮) menu for clinic activity notes. Previously gated on `activityType === 'Manual'` only;
   * APIs often send `manual`, different casing, or omit the field for older rows.
   * Nurse notes timeline: only the author may edit or delete.
   */
  showActivityNoteEditMenu(note: ActivityNoteDisplay | null | undefined): boolean {
    if (!note?.id) {
      return false;
    }
    if (this.isReferralCreatedNote(note)) {
      return false;
    }
    if (this.nurseNotesTimeline && !this.isActivityNoteOwnedByCurrentUser(note)) {
      return false;
    }
    const raw = String(note.activityType ?? '').trim();
    if (!raw) {
      return true;
    }
    const at = raw.toLowerCase();
    return (
      at === 'manual' ||
      at.includes('manual') ||
      at === 'note' ||
      at.includes('staff note') ||
      at.includes('activity note')
    );
  }

  isActivityNoteOwnedByCurrentUser(
    note: ActivityNoteDisplay | null | undefined
  ): boolean {
    if (!note) {
      return false;
    }

    const ext = note as ActivityNoteDisplay & Record<string, unknown>;
    const uidRaw =
      ext['userId'] ??
      ext['user_id'] ??
      ext['createdByUserId'] ??
      ext['created_by_user_id'] ??
      ext['activityByUserId'] ??
      ext['activity_by_user_id'];
    const uid = typeof uidRaw === 'string' ? uidRaw.trim() : '';
    if (uid && this.loggedInUserId) {
      return uid === this.loggedInUserId;
    }

    const author = String(note.activityBy || '').trim();
    const currentUser = String(this.loggedInUserName || '').trim();
    if (!author || !currentUser) {
      return false;
    }

    return (
      this.normalizePersonNameForCompare(author) ===
      this.normalizePersonNameForCompare(currentUser)
    );
  }

  private findActivityNoteById(id: string): ActivityNoteDisplay | null {
    const item = this.mergedItems.find(
      (it) =>
        it.source === 'activity' &&
        (it.data as ActivityNoteDisplay).id === id
    );
    return item ? (item.data as ActivityNoteDisplay) : null;
  }

  /** “Dismissed” line under call activities after clear (wa-engage-v2 parity). */
  engageActivityDismissedByLine(a: EngageActivity): boolean {
    if (a.notification !== 0) {
      return false;
    }
    const t = (a.type || '').toLowerCase();
    return (
      t === 'missed' ||
      t === 'missed_call' ||
      t === 'inbound' ||
      t === 'outbound' ||
      t === 'voicemail'
    );
  }

  /** Missed / inbound / outbound / voicemail — wa-engage-v2 leads activity dismiss (notification → 0). */
  engageActivityShowDismissCalls(a: EngageActivity): boolean {
    if (a.notification !== 1) {
      return false;
    }
    const t = (a.type || '').toLowerCase();
    return (
      t === 'missed' ||
      t === 'missed_call' ||
      t === 'inbound' ||
      t === 'outbound' ||
      t === 'voicemail'
    );
  }

  private onEngageMessageNewSocket(raw: any): void {
    const rid = (this.referralId || '').trim();
    const msgRid = raw?.referralId != null ? String(raw.referralId).trim() : '';
    const pid = (this.patientId || '').trim();
    const msgPid = raw?.patientId != null ? String(raw.patientId).trim() : '';

    if (rid) {
      if (!msgRid || msgRid !== rid) {
        return;
      }
      if (pid && msgPid && msgPid !== pid) {
        return;
      }
    } else {
      if (!pid || !msgPid || msgPid !== pid) {
        return;
      }
    }

    const mapped = this.mapSocketPayloadToTwilioMessage(raw);
    this.mergedItems.push({
      source: 'sms',
      sortDate: new Date(mapped.createdAt || '').getTime() || Date.now(),
      data: mapped,
    });
    this.mergedItems.sort((a, b) => this.compareTimelineItems(a, b));
    this.syncEngageReferralListActivityFromMerged();
    this.resolveSmsNames([mapped]);
    if (this.shouldAutoScrollToBottom() && !this.userHasScrolledUp) {
      this.scrollToBottomRequested = true;
    }
    this.chatNamesReady.emit(this.getNamesFromMergedItems(this.mergedItems));
    this._eventService.broadcast(BroadcastEventTypes.SmsMessagesRefresh);
    this.emitTimelineMerged();
    this._cdr.markForCheck();
  }

  private mapSocketPayloadToTwilioMessage(raw: any): TwilioMessage {
    const created =
      raw.createdAt ?? raw.created_at ?? raw.timestamp ?? new Date().toISOString();
    const bodyRaw = raw.body ?? raw.message ?? raw.text ?? '';
    const nameRaw = raw.senderName ?? raw.name ?? '';
    const rec = this.resolveRecordingUrlFromPayload(raw as Record<string, unknown>);
    const msg: TwilioMessage = {
      id: raw.id ?? raw.messageId ?? raw.sid,
      referralId: raw.referralId,
      patientId: (raw.patientId ?? this.patientId ?? '') as string,
      body: this.decodeDisplayText(bodyRaw) || bodyRaw,
      senderName: this.decodeDisplayText(nameRaw) || nameRaw,
      firstName: raw.firstName,
      lastName: raw.lastName,
      userId: raw.userId,
      callType: raw.callType,
      messageType: raw.messageType ?? raw.direction,
      createdAt: typeof created === 'string' ? created : new Date(created).toISOString(),
      recordingUrl: rec || raw.recordingUrl,
      mediaUrl: raw.mediaUrl ?? raw.media_url,
      mediaContentType: raw.mediaContentType ?? raw.media_content_type,
      mediaFileName: raw.mediaFileName ?? raw.media_filename ?? raw.mediaFilename,
      mediaFilename: raw.mediaFilename ?? raw.media_file_name ?? raw.mediaFileName,
    };
    return this.normalizeTwilioMmsFields(msg);
  }

  /** Worklist row badge channel cleared when an activity notification is dismissed. */
  private worksheetDismissChannelForActivity(
    activity: EngageActivity
  ): WorksheetEngageBadgeDismissChannel | null {
    const t = String(activity.type || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");
    if (t === "form_filled") {
      return "notifications";
    }
    if (
      t === "missed" ||
      t === "missed_call" ||
      t === "inbound" ||
      t === "voicemail"
    ) {
      return "notifications";
    }
    return null;
  }

  /** `nextNotification` optional — when set, used instead of toggling (call / voicemail dismiss). */
  resetNotification(activity: EngageActivity, nextNotification?: 0 | 1): void {
    if (!activity?.id) return;
    const notification: 0 | 1 =
      nextNotification !== undefined
        ? nextNotification
        : activity.notification === 0
          ? 1
          : 0;
    this._engageActivityService.resetNotifications(activity.id, notification)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const item = this.mergedItems.find((x) => x.source === 'engage' && (x.data as EngageActivity).id === activity.id);
          if (item) {
            const data = item.data as EngageActivity;
            const ext = data as Record<string, unknown>;
            data.notification = notification;
            if (notification === 0 && response) {
              const r = response as Record<string, unknown>;
              const idCandidate =
                (typeof r['notificationClearedByUserId'] === 'string' && r['notificationClearedByUserId'].trim()) ||
                (typeof r['notification_cleared_by_user_id'] === 'string' && r['notification_cleared_by_user_id'].trim()) ||
                (typeof r['clearedByUserId'] === 'string' && r['clearedByUserId'].trim()) ||
                '';
              const ncbRaw = r['notificationClearedBy'] ?? r['notification_cleared_by'];
              const ncbStr = typeof ncbRaw === 'string' ? ncbRaw.trim() : '';
              const userId =
                idCandidate || (ncbStr && this.looksLikeUuid(ncbStr) ? ncbStr : '');
              if (userId) {
                (data as any).notificationClearedByUserId = userId;
                ext['notificationClearedByUserId'] = userId;
              }
              const resolved = userId ? this.resolveUserIdToDisplayName(userId) : null;
              let displayName = resolved || '';
              if (!displayName && ncbStr && !this.looksLikeUuid(ncbStr)) {
                displayName = this.decodeDisplayText(ncbStr) || ncbStr;
              }
              if (!displayName && ncbStr) {
                displayName = ncbStr;
              }
              if (displayName) {
                (data as any).notificationClearedBy = displayName;
              }
            }
          }
          const cacheAppt = this._applicationCacheService.applicationCache.appointment;
          const rid = (
            this.referralId ||
            getAppointmentReferralId(cacheAppt) ||
            ''
          ).trim();
          const pid = (
            this.patientId ||
            activity.patientId ||
            cacheAppt?.patientId ||
            cacheAppt?.encountersPatient?.id ||
            ''
          )
            .toString()
            .trim();
          const encId = (this.encounterId || '').trim();
          if (notification === 0) {
            const channel = this.worksheetDismissChannelForActivity(activity);
            if (channel) {
              this._admissionService.applyWorksheetEngageBadgeDismiss({
                referralId: rid || undefined,
                encounterId: encId || undefined,
                channel,
              });
            }
          }
          this._eventService.broadcast(BroadcastEventTypes.EngageSidebarCountsRefresh, {
            referralId: rid || undefined,
            patientId: pid || undefined,
            activityType: String(activity.type || '').trim() || undefined,
            /** So Engage sidebar row drops `engage-list-item--has-notifications` when count hits 0. */
            dismissNotification: notification === 0,
          });
          this._cdr.markForCheck();
        },
        error: (err) =>
          this._toastService.error(err?.error?.message || 'Failed to update notification.', 'Error!'),
      });
  }

  startEditReminder(activity: EngageActivity): void {
    if (activity.type !== 'reminder' || !activity.id) return;
    this.editingActivityId = activity.id;
    const reminderId = (activity as any).reminderId;
    if (!reminderId) {
      this._toastService.warning('Reminder ID not found. Cannot edit this reminder.');
      return;
    }
    this.editingReminderId = reminderId;
    this.loadingReminderDetails = true;
    this._remindersService.getReminderById(reminderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (reminder) => {
          this.loadingReminderDetails = false;
          const isEveryone = !reminder.assignedUserId && !reminder.assignedToResourceId &&
            (!reminder.assignedTo || reminder.assignedTo === 'everyone' || reminder.assignedTo?.toLowerCase() === 'everyone');
          const assignedId = isEveryone ? 'everyone' : (
            reminder.assignedToResourceId ?? reminder.assignedUserId ??
            this.resources.find((r) => r.displayName === reminder.assignedTo)?.value ??
            this.resources.find((r) => r.value === reminder.assignedUserId)?.value ??
            this.resources[0]?.value ?? 'everyone'
          );
          this.reminderEditForm = {
            assignedToResourceId: assignedId || 'everyone',
            dueDate: reminder.dueDate ? new Date(reminder.dueDate) : new Date(),
            time: this.parseTimeToInput(reminder.time ?? reminder.dueTime ?? '08:00'),
            reminderOption: String(reminder.reminderOffsets ?? reminder.reminderOption ?? '15'),
            timezone: this.resolveTimezoneForDropdown(reminder.timezone)
          };
          this._cdr.markForCheck();
        },
        error: (err) => {
          this.loadingReminderDetails = false;
          this._toastService.error(err?.error?.message || 'Failed to load reminder details.', 'Error!');
          this.cancelEditReminder();
          this._cdr.markForCheck();
        }
      });
  }

  /** Match API timezone (e.g. Asia/Karachi) to dropdown value (e.g. Asia/Karachi (+0500 UTC+5)) */
  private resolveTimezoneForDropdown(apiTz?: string): string {
    if (!apiTz || !apiTz.trim()) return this.timezones[0] ?? '';
    const tz = apiTz.trim();
    const match = this.timezones.find((t) => t.startsWith(tz) || tz.startsWith(t.split(/\s+/)[0]));
    return match ?? this.timezones[0] ?? '';
  }

  cancelEditReminder(): void {
    this.editingReminderId = null;
    this.editingActivityId = null;
    this.loadingReminderDetails = false;
  }

  openReminderTimePicker(): void {
    this.reminderTimeInputRef?.nativeElement?.click();
  }

  saveReminder(): void {
    if (!this.editingReminderId || !this.patientId) return;
    const isEveryone = this.reminderEditForm.assignedToResourceId === 'everyone';
    const assignedTo = isEveryone ? 'everyone' : 'me';
    const assignedUserId = isEveryone ? this.loggedInUserId : (this.reminderEditForm.assignedToResourceId || this.loggedInUserId);
    const d = this.reminderEditForm.dueDate;
    const dueDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const timezone = this.getIanaTimezone(this.reminderEditForm.timezone);
    const payload: Record<string, any> = {
      assignedUserId,
      assignedTo,
      dueDate,
      dueTime: this.reminderEditForm.time,
      reminderOffsets: this.reminderEditForm.reminderOption,
      status: 'pending',
      timezone
    };
    this._remindersService.updateReminder(this.editingReminderId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._toastService.success('Reminder updated.');
          this.editingReminderId = null;
          this.editingActivityId = null;
          this.loadAll();
        },
        error: (err) =>
          this._toastService.error(err?.error?.message || 'Failed to update reminder.', 'Error!'),
      });
  }

  /** Extract IANA timezone (e.g. Asia/Karachi) - API expects format without offset suffix */
  private getIanaTimezone(tz: string): string {
    if (!tz || !tz.trim()) return 'Asia/Karachi';
    const trimmed = tz.trim();
    const firstPart = trimmed.split(/\s+/)[0];
    return firstPart || 'Asia/Karachi';
  }

  private parseTimeToInput(timeStr: string): string {
    const match = String(timeStr).match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (match) {
      let h = parseInt(match[1], 10);
      const m = match[2];
      if (match[3]?.toUpperCase() === 'PM' && h < 12) h += 12;
      if (match[3]?.toUpperCase() === 'AM' && h === 12) h = 0;
      return `${h.toString().padStart(2, '0')}:${m}`;
    }
    return '08:00';
  }

  private formatTimeForDisplay(t: string): string {
    if (!t) return '08:00 AM';
    const parts = t.split(':').map(Number);
    const hours = parts[0] ?? 0;
    const minutes = parts[1] ?? 0;
    const period = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    const m = minutes.toString().padStart(2, '0');
    return `${h.toString().padStart(2, '0')}:${m} ${period}`;
  }

  // Activity notes
  removeActivityNote(id: string): void {
    const note = this.findActivityNoteById(id);
    if (
      note &&
      this.nurseNotesTimeline &&
      !this.isActivityNoteOwnedByCurrentUser(note)
    ) {
      return;
    }

    Swal.fire({
      title: 'Delete Activity Note?',
      text: 'This Activity Note will be removed. Proceed?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#513DCA',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.idBeingDeleted = id;
      this._activityNotesService.deleteActivityNoteById(id).subscribe({
        next: () => {
          this.mergedItems = this.mergedItems.filter((it) => !(it.source === 'activity' && (it.data as ActivityNoteDisplay).id === id));
          this._cdr.markForCheck();
        },
        error: (err) =>
          this._toastService.error(err || 'Unable to delete activity note.', 'Error!'),
        complete: () => {
          this.idBeingDeleted = null;
          this._cdr.markForCheck();
        }
      });
    });
  }

  editActivityNote(activityNote: ActivityNoteDisplay): void {
    if (
      this.nurseNotesTimeline &&
      !this.isActivityNoteOwnedByCurrentUser(activityNote)
    ) {
      return;
    }

    if (this.nurseNotesTimeline) {
      this.startNurseNoteInlineEdit(activityNote);
      return;
    }
    const options: ModalOptions = {
      initialState: {
        activityNote: cloneDeep(activityNote),
        loggedInUserName: this.loggedInUserName
      },
      class: 'modal-md child-modal modal-dialog-scrollable',
      ignoreBackdropClick: true
    };
    const bsModalRef = this._modalService.show(UpdateActivityNoteComponent, options);
    bsModalRef.content.onClose.subscribe((updated: ActivityNote) => {
      if (!updated) return;
      const idx = this.mergedItems.findIndex((it) => it.source === 'activity' && (it.data as ActivityNoteDisplay).id === updated.id);
      if (idx !== -1) {
        this.mergedItems[idx] = { ...this.mergedItems[idx], data: this.mapNoteToDisplayNote(updated) };
        this._cdr.markForCheck();
      }
    });
  }

  isEditingNurseNote(note: ActivityNoteDisplay | null | undefined): boolean {
    return (
      this.nurseNotesTimeline &&
      !!note?.id &&
      this.editingNurseNoteId === note.id
    );
  }

  startNurseNoteInlineEdit(note: ActivityNoteDisplay): void {
    if (!note?.id) {
      return;
    }
    if (!this.isActivityNoteOwnedByCurrentUser(note)) {
      return;
    }

    const { date, time } = this.parseNurseNoteEditDateTime(note.createdAt);
    this.editingNurseNoteId = note.id;
    this.editingNurseNoteText = note.notes || "";
    this.editingNurseNoteDate = date;
    this.editingNurseNoteTime = time;
    this._cdr.markForCheck();
  }

  cancelNurseNoteInlineEdit(): void {
    this.editingNurseNoteId = null;
    this.editingNurseNoteText = "";
    this.editingNurseNoteDate = "";
    this.editingNurseNoteTime = "";
    this.savingNurseNoteEdit = false;
    this._cdr.markForCheck();
  }

  get canSaveNurseNoteInlineEdit(): boolean {
    return !!(
      (this.editingNurseNoteText || "").trim() &&
      (this.editingNurseNoteDate || "").trim() &&
      (this.editingNurseNoteTime || "").trim()
    );
  }

  private parseNurseNoteEditDateTime(createdAt: string | null | undefined): {
    date: string;
    time: string;
  } {
    const raw = String(createdAt || "").trim();
    const dt = raw ? new Date(raw) : new Date();
    if (Number.isNaN(dt.getTime())) {
      return { date: "", time: "" };
    }
    return {
      date: this.toNurseNoteDateInputValue(dt),
      time: this.toNurseNoteTimeInputValue(dt),
    };
  }

  private toNurseNoteDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private toNurseNoteTimeInputValue(date: Date): string {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  private buildNurseNoteEditCreatedAtIso(): string | null {
    const datePart = String(this.editingNurseNoteDate || "").trim();
    const timePart = String(this.editingNurseNoteTime || "").trim();
    if (!datePart || !timePart) {
      return null;
    }
    const dt = new Date(`${datePart}T${timePart}:00`);
    if (Number.isNaN(dt.getTime())) {
      return null;
    }
    return dt.toISOString();
  }

  onNurseNoteInlineEditKeydown(
    event: KeyboardEvent,
    note: ActivityNoteDisplay
  ): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.cancelNurseNoteInlineEdit();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.saveNurseNoteInlineEdit(note);
    }
  }

  saveNurseNoteInlineEdit(note: ActivityNoteDisplay): void {
    const text = (this.editingNurseNoteText || "").trim();
    const createdAt = this.buildNurseNoteEditCreatedAtIso();
    if (
      !text ||
      !createdAt ||
      !this.canSaveNurseNoteInlineEdit ||
      this.savingNurseNoteEdit ||
      !note?.id ||
      !this.isActivityNoteOwnedByCurrentUser(note)
    ) {
      return;
    }
    this.savingNurseNoteEdit = true;
    this._activityNotesService
      .updateActivityNote(note.id, {
        patientId: note.patientId,
        procedureId: note.procedureId,
        notes: text,
        activityBy: this.loggedInUserName,
        activityType: "Manual",
        isNurseNote: true,
        createdAt,
      })
      .subscribe({
        next: (updated: ActivityNote) => {
          const idx = this.mergedItems.findIndex(
            (it) =>
              it.source === "activity" &&
              (it.data as ActivityNoteDisplay).id === updated.id
          );
          if (idx !== -1) {
            const display = this.mapNoteToDisplayNote(updated);
            this.mergedItems[idx] = {
              ...this.mergedItems[idx],
              sortDate: new Date(display.createdAt || "").getTime(),
              data: display,
            };
            this.mergedItems.sort((a, b) => this.compareTimelineItems(a, b));
          }
          this.cancelNurseNoteInlineEdit();
          this._cdr.markForCheck();
        },
        error: () => {},
        complete: () => {
          this.savingNurseNoteEdit = false;
          this._cdr.markForCheck();
        },
      });
  }

  // SMS
  formatMessageDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Patient / lead name from application cache (Engage selected referral or clinic patient).
   * Used when Twilio/activity rows omit caller name for voicemail & missed calls.
   */
  private getCachedContextPatientDisplayName(): string {
    const p = this._applicationCacheService.applicationCache.patient;
    if (!p) {
      return '';
    }
    const pid = (this.patientId || '').trim();
    const cacheId = (p.id || '').trim();
    if (pid && cacheId && pid !== cacheId) {
      return '';
    }
    const first = this.decodeDisplayText(p.firstName || '') || String(p.firstName || '').trim();
    const last = this.decodeDisplayText(p.lastName || '') || String(p.lastName || '').trim();
    const name = `${first} ${last}`.trim();
    return name;
  }

  getMessageDisplayName(message: TwilioMessage): string {
    if (message.firstName != null && message.lastName != null && (message.firstName || message.lastName)) {
      return this.decodeDisplayText(`${message.firstName} ${message.lastName}`.trim()) || `${message.firstName} ${message.lastName}`.trim();
    }
    const user = message.userId ? this.companyUsersMap[message.userId] : null;
    if (user && (user.firstName || user.lastName)) {
      return `${user.firstName} ${user.lastName}`.trim();
    }
    const sender = String(message.senderName || '').trim();
    if (sender && sender.toLowerCase() !== 'unknown') {
      return this.decodeDisplayText(sender) || sender;
    }
    /** Left / inbound / voicemail / missed: receiving side is the lead or patient — not "Unknown". */
    if (!this.isSentMessage(message)) {
      const cached = this.getCachedContextPatientDisplayName();
      if (cached) {
        return cached;
      }
      return 'Patient';
    }
    const fallback = message.senderName || 'Unknown';
    return this.decodeDisplayText(fallback) || fallback;
  }

  /**
   * Title above left-column Engage cards (replaces brittle inline ternary in template).
   */
  getEngageLeftColumnTitle(a: EngageActivity): string {
    const t = String(a.type || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    if (t === 'form_filled') {
      return '';
    }
    if (t === 'voicemail' || t === 'missed' || t === 'missed_call' || t === 'inbound') {
      const by = String(a.activityBy || '').trim();
      if (by && by.toLowerCase() !== 'unknown') {
        return by;
      }
      return this.getCachedContextPatientDisplayName() || 'Patient';
    }
    if (this.isEngageDripType(t)) {
      return String(a.activityBy || '').trim();
    }
    return String(a.activityBy || '').trim();
  }

  /** UUID v4 shape — activity APIs often send user ids for who dismissed notifications. */
  private looksLikeUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test((value || '').trim());
  }

  private resolveUserIdToDisplayName(userId: string | undefined | null): string | null {
    const id = (userId || '').trim();
    if (!id) return null;
    let u = this.companyUsersMap[id];
    if (!u) {
      const lower = id.toLowerCase();
      for (const [k, v] of Object.entries(this.companyUsersMap)) {
        if (k.toLowerCase() === lower) {
          u = v;
          break;
        }
      }
    }
    if (u && (u.firstName || u.lastName)) {
      return `${u.firstName || ''} ${u.lastName || ''}`.trim();
    }
    return null;
  }

  /**
   * GET /reminders row: map assignee ids to display (company users + resources).
   * @param treatMissingAssigneeAsEveryone when true, empty assignee → "Everyone" (reminders API semantics).
   */
  private resolveReminderAssigneeDisplayFromApiRow(
    m: Record<string, unknown>,
    treatMissingAssigneeAsEveryone = true
  ): string {
    const src = this.flattenEngageReminderEnrichmentSource(m);
    const assignedToStr = String(src['assignedTo'] ?? src['assigned_to'] ?? '').trim();
    const lower = assignedToStr.toLowerCase();
    const resourceId = String(
      src['assignedToResourceId'] ?? src['assigned_to_resource_id'] ?? ''
    ).trim();
    const userId = String(
      src['assignedUserId'] ??
        src['assigned_user_id'] ??
        src['AssignedUserId'] ??
        ''
    ).trim();

    const isEveryone =
      treatMissingAssigneeAsEveryone &&
      !userId &&
      !resourceId &&
      (!assignedToStr || lower === 'everyone' || lower === '');
    if (isEveryone) {
      return this.assignedToEveryoneOption.displayName;
    }
    if (userId && this.looksLikeUuid(userId)) {
      const fromMap = this.resolveUserIdToDisplayName(userId);
      if (fromMap) {
        return fromMap;
      }
    }
    if (resourceId) {
      const res = this.resources.find(
        (r) => r.value === resourceId || r.value?.toLowerCase() === resourceId.toLowerCase()
      );
      if (res?.displayName?.trim()) {
        return res.displayName.trim();
      }
      if (resourceId && this.looksLikeUuid(resourceId)) {
        const fromMap = this.resolveUserIdToDisplayName(resourceId);
        if (fromMap) {
          return fromMap;
        }
      }
    }
    if (assignedToStr && assignedToStr.toLowerCase() !== 'unknown') {
      if (!this.looksLikeUuid(assignedToStr)) {
        return this.decodeDisplayText(assignedToStr) || assignedToStr;
      }
      const fromMap = this.resolveUserIdToDisplayName(assignedToStr);
      if (fromMap) {
        return fromMap;
      }
    }
    if (userId && !this.looksLikeUuid(userId)) {
      return this.decodeDisplayText(userId) || userId;
    }
    return '';
  }

  /** Engage activity only — do not default missing assignee to Everyone. */
  private resolveReminderAssigneeFromEngageActivityOnly(raw: EngageActivity): string {
    return this.resolveReminderAssigneeDisplayFromApiRow(
      this.flattenEngageReminderEnrichmentSource(raw as Record<string, unknown>),
      false
    );
  }

  /** Title above reminder cards — never show the literal "Unknown". */
  getReminderUnifiedTitle(a: EngageActivity | null | undefined): string {
    if (!a) {
      return '';
    }
    const t = String(a.type || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    if (t !== 'reminder') {
      return String(a.activityBy || '').trim();
    }
    const cur = String(a.activityBy || '').trim();
    if (cur && cur.toLowerCase() !== 'unknown') {
      return cur;
    }
    const fromEngage = this.resolveReminderAssigneeFromEngageActivityOnly(a);
    if (fromEngage) {
      return fromEngage;
    }
    const nm = String(a.name || '').trim();
    if (nm && nm.toLowerCase() !== 'unknown') {
      return this.decodeDisplayText(nm) || nm;
    }
    const re = this.extractEngageActivityAuthorRaw(a);
    if (re && re.toLowerCase() !== 'unknown') {
      return re;
    }
    if (this.loggedInUserName?.trim()) {
      return this.loggedInUserName.trim();
    }
    return '';
  }

  /**
   * "Dismissed — …" line: API may send `notificationClearedBy` / `notificationClearedByUserId` as a
   * user id; resolve to first/last via `companyUsersMap` like join_conversation SMS rows.
   */
  getNotificationClearedByDisplay(activity: EngageActivity): string {
    if (!activity) return '';
    const ext = activity as Record<string, unknown>;
    const fromUserId =
      (typeof ext['notificationClearedByUserId'] === 'string' && ext['notificationClearedByUserId'].trim()) ||
      (typeof ext['notification_cleared_by_user_id'] === 'string' && ext['notification_cleared_by_user_id'].trim()) ||
      '';
    if (fromUserId && this.looksLikeUuid(fromUserId)) {
      const resolved = this.resolveUserIdToDisplayName(fromUserId);
      if (resolved) return resolved;
    }
    const ncb = ext['notificationClearedBy'] ?? ext['notification_cleared_by'];
    if (typeof ncb !== 'string' || !ncb.trim()) return '';
    const s = ncb.trim();
    if (this.looksLikeUuid(s)) {
      const resolved = this.resolveUserIdToDisplayName(s);
      return resolved || s;
    }
    return this.decodeDisplayText(s) || s;
  }

  isSentMessage(message: TwilioMessage): boolean {
    const isSent = message.messageType === 'sent' || message.messageType === 'outbound' || message.callType === 'outbound';
    return isSent;
  }

  /**
   * SMS / Twilio row alignment: staff/clinic outbound on the right; patient inbound, voicemail, missed on the left.
   */
  getSmsTimelineAlign(m: TwilioMessage): 'right' | 'left' | 'center' {
    if (this.isJoinConversationTwilioMessage(m)) {
      return 'center';
    }
    if (this.isSentMessage(m)) {
      return 'right';
    }
    return 'left';
  }

  /** Join events sometimes omit messageType; match legacy sms-timeline behavior (centered system line). */
  private isJoinConversationTwilioMessage(m: TwilioMessage): boolean {
    const mt = String(m.messageType || '').toLowerCase().replace(/\s+/g, '_');
    const ct = String(m.callType || '').toLowerCase().replace(/\s+/g, '_');
    if (mt === 'join_conversation' || ct === 'join_conversation') {
      return true;
    }
    const body = String(m.body || '').toLowerCase();
    return body.includes('joined the conversation');
  }

  /**
   * Join-the-conversation rows from Engage activity API (not Twilio) — show centered, not a chat bubble.
   */
  isEngageJoinConversationRow(a: EngageActivity): boolean {
    if (this.isEngageSmsActivity(a)) {
      return false;
    }
    const t = this.resolveEngageActivityTypeToken(a);
    if (t === 'join_conversation' || t === 'join') {
      return true;
    }
    const notes = String(a.notes || a.comment || '').toLowerCase();
    return notes.includes('joined the conversation');
  }

  getEngageJoinDisplayName(a: EngageActivity): string {
    const raw = String(a.activityBy || a.name || '').trim();
    return this.decodeDisplayText(raw) || raw || 'Someone';
  }

  /** Label for left-aligned SMS/Twilio rows (received / inbound). */
  getReceivedSmsRowLabel(m: TwilioMessage): string {
    const ct = String(m.callType || '').toLowerCase();
    if (ct === 'inbound') {
      return 'Inbound Call';
    }
    if (ct === 'voicemail') {
      return 'Voicemail';
    }
    if (ct === 'missed' || ct === 'missed_call') {
      return 'Missed Call';
    }
    if (ct) {
      return 'Call';
    }
    return 'SMS';
  }

  private normalizePersonNameForCompare(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  /** True if display name matches the logged-in user or any company directory user. */
  displayNameMatchesCompanyUser(displayName: string): boolean {
    const norm = this.normalizePersonNameForCompare(displayName);
    if (!norm || norm === 'unknown') {
      return false;
    }
    if (norm === this.normalizePersonNameForCompare(this.loggedInUserName)) {
      return true;
    }
    for (const u of Object.values(this.companyUsersMap)) {
      const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
      if (full && norm === this.normalizePersonNameForCompare(full)) {
        return true;
      }
    }
    return false;
  }

  /**
   * True when activity author is a company user (optional API user id fields).
   */
  isCompanyUserActivityAuthor(a: EngageActivity): boolean {
    const ext = a as Record<string, unknown>;
    const uidRaw =
      ext['userId'] ?? ext['user_id'] ?? ext['createdByUserId'] ?? ext['created_by_user_id'];
    const uid = typeof uidRaw === 'string' ? uidRaw.trim() : '';
    if (uid && (this.companyUsersMap[uid] || uid === this.loggedInUserId)) {
      return true;
    }
    const by = String(a.activityBy || a.name || ext['createdBy'] || '').trim();
    return this.displayNameMatchesCompanyUser(by);
  }

  /**
   * Engage timeline: right = sent by clinic/staff or outbound; left = received from patient/lead.
   */
  isEngageActivityAlignRight(a: EngageActivity): boolean {
    const t = this.resolveEngageActivityTypeToken(a);
    if (
      t === 'inbound' ||
      t === 'voicemail' ||
      t === 'missed' ||
      t === 'missed_call' ||
      t === 'form_filled'
    ) {
      return false;
    }
    if (t === 'outbound') {
      return true;
    }
    if (this.isEngageDripType(t)) {
      return true;
    }
    if (t === 'reminder') {
      return true;
    }
    if (t === 'note' || t === 'sms') {
      return this.isCompanyUserActivityAuthor(a);
    }
    if (
      t === 'schedule' ||
      t === 'stage' ||
      t === 'call' ||
      t === 'checked_in' ||
      t === ''
    ) {
      return this.isCompanyUserActivityAuthor(a);
    }
    return this.isCompanyUserActivityAuthor(a);
  }

  /** Internal activity note: right if company user or referral-created (`req.svg` row). */
  isActivityNoteAlignRight(note: ActivityNoteDisplay): boolean {
    if (this.isReferralCreatedNote(note)) {
      return true;
    }
    return this.displayNameMatchesCompanyUser(note.activityBy || '');
  }

  /** Prospect History — full-width rows; skip SMS-style right rail. */
  shouldEngageActivityAlignRight(a: EngageActivity): boolean {
    return !this.fullWidthTimeline && this.isEngageActivityAlignRight(a);
  }

  activityNoteTimelineSide(note: ActivityNoteDisplay): 'right' | 'left' {
    if (this.fullWidthTimeline) {
      return 'left';
    }
    return this.isActivityNoteAlignRight(note) ? 'right' : 'left';
  }

  smsTimelineSide(message: TwilioMessage): 'right' | 'left' | 'center' {
    if (this.fullWidthTimeline) {
      return this.getSmsTimelineAlign(message) === 'center' ? 'center' : 'left';
    }
    return this.getSmsTimelineAlign(message);
  }

  engageStandardRowUsesW80(activity: EngageActivity): boolean {
    if (this.fullWidthTimeline) {
      return false;
    }
    const t = String(activity.type ?? '').toLowerCase();
    return t !== 'reminder' || this.editingActivityId !== activity.id;
  }

  isOutboundCall(message: TwilioMessage): boolean {
    return message.callType === 'outbound' || (message.callType && message.callType.toLowerCase().includes('outbound'));
  }

  /**
   * Body next to the type label on Engage cards (left/right).
   * Comment/notes when present; otherwise created datetime (`mm/dd/yyyy HH:mm:ss`).
   */
  getEngageActivityCardBody(a: EngageActivity | null | undefined): string {
    if (!a) {
      return '';
    }
    const type = String(a.type || '');
    if (isEngageAppointmentConfirmedType(type)) {
      const dateLine = extractEngageActivityAppointmentDateDisplay(
        a as Record<string, unknown>
      );
      if (dateLine) {
        return dateLine;
      }
      return formatEngageActivityCreatedDateTime(a as Record<string, unknown>);
    }
    const raw = String(a.notes ?? a.comment ?? '').trim();
    if (raw) {
      return raw;
    }
    return formatEngageActivityCreatedDateTime(a as Record<string, unknown>);
  }

  /** Engage card text row — show when body, stage, or type label applies. */
  shouldShowEngageActivityCard(a: EngageActivity | null | undefined): boolean {
    if (!a) {
      return false;
    }
    const t = this.normalizeTimelineIconToken(String(a.type || ''));
    if (t === 'stage' && String(a.stage || '').trim()) {
      return true;
    }
    if (this.getEngageActivityCardBody(a)) {
      return true;
    }
    return !!String(a.type || '').trim();
  }

  private isEngageDripType(type: string): boolean {
    const t = this.normalizeTimelineIconToken(type);
    return t === 'drip' || t.includes('drip');
  }

  getEngageTypeLabel(
    type: string | EngageActivity | Record<string, unknown> | null | undefined
  ): string {
    const raw =
      type && typeof type === 'object'
        ? this.resolveEngageActivityTypeToken(type) ||
          String((type as EngageActivity).type ?? '').trim()
        : String(type ?? '').trim();
    if (this.isEngageDripType(this.normalizeTimelineIconToken(raw))) {
      return 'Drip';
    }
    return engageActivityTypeDisplayLabel(raw);
  }

  /** Type prefix on Engage card body (`Manual` / `Note` / `Reminder` / added-comment rows omit the label). */
  shouldShowEngageTypeLabel(
    type: string | EngageActivity | Record<string, unknown> | null | undefined
  ): boolean {
    const t =
      type && typeof type === 'object'
        ? this.resolveEngageActivityTypeToken(type)
        : this.normalizeTimelineIconToken(String(type ?? ''));
    if (t === 'manual' || t.includes('manual')) {
      return false;
    }
    if (t === 'note' || t === 'reminder') {
      return false;
    }
    const rawType =
      type && typeof type === 'object'
        ? String(
            (type as Record<string, unknown>)['type'] ??
              (type as Record<string, unknown>)['activityType'] ??
              ''
          ).trim()
        : String(type ?? '').trim();
    if (isEngageAddedCommentType(rawType)) {
      return false;
    }
    return true;
  }

  /** Display label for clinic activity-note rows (`activityType`). */
  getActivityNoteTypeLabel(activityType: string | null | undefined): string {
    const raw = String(activityType ?? '').trim();
    if (!raw) {
      return 'Activity';
    }
    const normalized = raw.toLowerCase().replace(/[\s_-]+/g, '_');
    if (normalized === 'manual' || normalized === 'memo') {
      return 'Note';
    }
    if (normalized === 'comment') {
      return 'Comment';
    }
    if (isEngageAddedCommentType(raw)) {
      return 'Comment';
    }
    if (raw.includes('_')) {
      return humanizeActivityTypeToken(raw);
    }
    return raw;
  }

  shouldShowActivityNoteTypeLabel(note: ActivityNoteDisplay | null | undefined): boolean {
    if (!note || this.isReferralCreatedNote(note)) {
      return false;
    }
    const t = String(note.activityType ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '_');
    if (!t) {
      return false;
    }
    return t !== 'manual' && t !== 'note' && t !== 'memo' && !isEngageAddedCommentType(t);
  }

  getActivityNoteCardBody(note: ActivityNoteDisplay | null | undefined): string {
    if (!note) {
      return '';
    }
    const notes = String(note.notes ?? '').trim();
    if (notes) {
      return notes;
    }
    return formatEngageActivityCreatedDateTime({ createdAt: note.createdAt });
  }

  shouldShowActivityNoteCard(note: ActivityNoteDisplay | null | undefined): boolean {
    if (!note) {
      return false;
    }
    return !!this.getActivityNoteCardBody(note) || this.shouldShowActivityNoteTypeLabel(note);
  }

  getEngageIcon(type: string): string {
    const t = type || '';
    if (t === 'checked_in') return 'fa-solid fa-check-circle text-muted';
    // Avoid fa-play (reads as media control); left column uses stage.svg instead
    if (t === 'stage') return 'fa-solid fa-diagram-project text-success';
    return 'fa-solid fa-note-sticky';
  }

  private static readonly MISSED_CALL_ICON = 'assets/images/custom/miss.svg';

  /**
   * Any Twilio/Engage row that represents a missed / unanswered-style call uses the red miss.svg asset.
   */
  isMissedCallPayload(data: EngageActivity | TwilioMessage | null | undefined): boolean {
    if (!data) {
      return false;
    }
    const o = data as Record<string, unknown>;
    const engType = String((data as EngageActivity).type ?? '').toLowerCase().trim();
    if (engType === 'missed' || engType === 'missed_call') {
      return true;
    }
    const str = (...vals: unknown[]): string =>
      vals
        .filter((v) => v != null && String(v).trim() !== '')
        .map((v) => String(v).toLowerCase())
        .join(' ');
    const tw = data as TwilioMessage;
    const haystack = str(
      tw.callType,
      tw.messageType,
      o['callType'],
      o['call_type'],
      o['CallType'],
      o['messageType'],
      o['message_type'],
      o['direction'],
      o['callStatus'],
      o['call_status'],
      o['DialCallStatus'],
      o['dialCallStatus']
    );
    if (!haystack.trim()) {
      return false;
    }
    if (haystack.includes('missed') || haystack.includes('missed_call')) {
      return true;
    }
    if (
      haystack.includes('no-answer') ||
      haystack.includes('no_answer') ||
      haystack.includes('no answer')
    ) {
      return true;
    }
    return false;
  }

  getCallIcon(data: EngageActivity | TwilioMessage): string {
    if (this.isMissedCallPayload(data)) {
      return UnifiedActivityFeedComponent.MISSED_CALL_ICON;
    }
    return 'assets/images/custom/call.svg';
  }

  /**
   * Inbound / outbound call rows: FA phone + direction arrow (replaces generic call.svg).
   * Inbound: brand blue; outbound: darker green (see `.unified-feed-call-icon--*`).
   */
  getEngageCallIconFontAwesomeClasses(
    type: string,
    data?: EngageActivity | TwilioMessage,
  ): string | null {
    const eng = data as EngageActivity | undefined;
    const t =
      this.normalizeTimelineIconToken(type) ||
      this.normalizeTimelineIconToken(String(eng?.type ?? ''));
    if (this.isEngageDripType(t)) {
      return 'fa-solid fa-droplet text-primary';
    }
    const fromEng = this.normalizeTimelineIconToken(String(eng?.type ?? ''));
    if (t === 'reminder' || fromEng === 'reminder') {
      return 'fa-solid fa-alarm-clock text-muted';
    }
    const resolved = this.resolveCallDirectionVisual(type, data);
    return resolved?.mode === 'fa' ? resolved.faClasses : null;
  }

  /** Image fallback when {@link getEngageCallIconFontAwesomeClasses} returns null. */
  getTimelineAvatarImgSrc(
    type: string,
    data?: EngageActivity | TwilioMessage | ActivityNoteDisplay,
  ): string {
    const resolved = this.resolveCallDirectionVisual(type, data);
    if (resolved?.mode === 'img') {
      return resolved.src;
    }
    if (this.isTwilioSmsTimelineRow(data)) {
      return 'assets/images/custom/sms-inout.svg';
    }
    return this.getEngageIconLeft(type, data);
  }

  /** SMS chat rows only — clinic `activityType: Manual` and Engage `type: note` must use the note icon. */
  private isTwilioSmsTimelineRow(data: unknown): data is TwilioMessage {
    if (!data || typeof data !== 'object') {
      return false;
    }
    const rec = data as Record<string, unknown>;
    if (String(rec['type'] ?? '').trim()) {
      return false;
    }
    if (String(rec['activityType'] ?? rec['activity_type'] ?? '').trim()) {
      return false;
    }
    const callType = rec['callType'] ?? rec['call_type'];
    if (callType != null && String(callType).trim() !== '') {
      return false;
    }
    return rec['body'] != null || rec['messageType'] != null || rec['message_type'] != null;
  }

  /** SMS/Twilio avatar on the timeline (left or right). */
  getSmsTimelineAvatarSrc(m: TwilioMessage): string {
    const typ = this.timelineIconType(m) || String(m.callType || 'call');
    return this.getTimelineAvatarImgSrc(typ, m);
  }

  private normalizeTimelineIconToken(s: string): string {
    return String(s || '')
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, '_');
  }

  private resolveCallDirectionVisual(
    type: string,
    data?: EngageActivity | TwilioMessage,
  ): { mode: 'fa'; faClasses: string } | { mode: 'img'; src: string } | null {
    if (data && this.isMissedCallPayload(data)) {
      return { mode: 'fa', faClasses: 'fa-solid fa-phone-missed text-danger' };
    }

    const eng = data as EngageActivity | undefined;
    const tw = data as TwilioMessage | undefined;
    const tPass = this.normalizeTimelineIconToken(type);
    const fromEngage = this.normalizeTimelineIconToken(String(eng?.type ?? ''));
    const t = tPass || fromEngage;
    const callTypeRaw = String(tw?.callType ?? '').toLowerCase();

    const outboundish =
      t === 'outbound' ||
      t.includes('outbound') ||
      fromEngage.includes('outbound') ||
      callTypeRaw.includes('outbound');
    const inboundish =
      t === 'inbound' ||
      t.includes('inbound') ||
      fromEngage.includes('inbound') ||
      callTypeRaw.includes('inbound');
    const callLike =
      !!tw?.callType ||
      t === 'call' ||
      inboundish ||
      outboundish ||
      t.includes('call');

    if (!callLike) {
      return null;
    }

    if (outboundish && !inboundish) {
      return {
        mode: 'fa',
        faClasses:
          'fa-solid fa-phone-arrow-up-right unified-feed-call-icon unified-feed-call-icon--outbound',
      };
    }

    return {
      mode: 'fa',
      faClasses:
        'fa-solid fa-phone-arrow-down-left unified-feed-call-icon unified-feed-call-icon--inbound',
    };
  }

  /**
   * Engage rows use `type`; clinic activity notes ({@link ActivityNoteDisplay}) use `activityType` only.
   * Without this, note rows pass `undefined` and avatar sizing breaks when `type === 'note'` used empty img width.
   */
  timelineIconType(data: unknown): string {
    const d = data as (EngageActivity & ActivityNoteDisplay & Record<string, unknown>) | null | undefined;
    if (!d) {
      return '';
    }
    const typ = String(d.type ?? '').trim();
    if (typ) {
      const normTyp = typ.toLowerCase().replace(/[\s-]+/g, '_');
      if (normTyp === 'sms') {
        return 'sms';
      }
      return this.isEngageDripType(typ) ? 'drip' : typ;
    }
    const at = String(d.activityType ?? d['activity_type'] ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    if (at === 'sms') {
      return 'sms';
    }
    if (this.isEngageDripType(at)) {
      return 'drip';
    }
    if (isReferralCreatedActivityType(at)) {
      return 'form_filled';
    }
    if (activityTypeUsesNoteIcon(at)) {
      return 'note';
    }
    return at;
  }

  getEngageIconLeft(type: string, data?: EngageActivity | TwilioMessage): string {
    const norm = (s: string) =>
      String(s || '')
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, '_');

    const eng = data as EngageActivity | undefined;
    const tPass = norm(type);
    const fromEngage = norm(String(eng?.type ?? ''));
    const t = tPass || fromEngage;

    if (t === 'schedule') {
      return 'assets/images/custom/cal.svg';
    }
    if (t === 'drip') {
      return 'assets/images/custom/drip.svg';
    }
    if (t === 'checked_in') {
      return 'assets/images/custom/checked_in.svg';
    }

    /** Dedicated missed rows (before voicemail / generic call matching). */
    if (t === 'missed' || t === 'missed_call') {
      return UnifiedActivityFeedComponent.MISSED_CALL_ICON;
    }

    if (t === 'voicemail') {
      if (data && this.isMissedCallPayload(data)) {
        return UnifiedActivityFeedComponent.MISSED_CALL_ICON;
      }
      return data && this.showCallRecordingPlayer(data)
        ? 'assets/images/custom/outbound-voice.svg'
        : 'assets/images/custom/vm.svg';
    }

    /** Calls: normalize API variants (`Outbound`, `outbound_call`, …) so icons match the timeline + referrals list. */
    const outboundish =
      t === 'outbound' || t.includes('outbound') || fromEngage.includes('outbound');
    const callLike =
      t === 'call' ||
      t === 'inbound' ||
      outboundish ||
      t.includes('inbound') ||
      fromEngage.includes('inbound');

    if (callLike) {
      const visual = this.resolveCallDirectionVisual(type, data);
      if (visual?.mode === 'img') {
        return visual.src;
      }
      if (visual?.mode === 'fa') {
        return 'assets/images/custom/call.svg';
      }
    }

    if (t === 'sms') {
      return 'assets/images/custom/sms-inout.svg';
    }
    if (t === 'reminder') {
      return 'assets/images/custom/reminder.svg';
    }
    if (t === 'form_filled') {
      return 'assets/images/custom/req.svg';
    }
    if (t === 'note') {
      return 'assets/images/custom/note.svg';
    }
    if (t === 'stage') {
      return 'assets/images/custom/stage.svg';
    }
    return 'assets/images/custom/note.svg';
  }

  showCallRecordingPlayer(data: EngageActivity | TwilioMessage): boolean {
    const url = this.getRecordingUrl(data);
    if (!url) {
      return false;
    }
    const tw = data as TwilioMessage;
    const en = data as EngageActivity;
    const ct = String(tw.callType ?? en.type ?? '').toLowerCase();
    const recordableTypes = [
      'inbound',
      'outbound',
      'call',
      'voicemail',
      'missed',
      'missed_call'
    ];
    return recordableTypes.includes(ct);
  }

  getRecordingUrl(data: EngageActivity | TwilioMessage): string {
    const direct = this.resolveRecordingUrlFromPayload(data);
    if (direct) {
      return direct;
    }
    return String((data as any).recordingUrl ?? '').trim();
  }

  isRecordingPlaying(url: string): boolean {
    return !!url && this.currentPlayingUrl === url;
  }

  getRecordingDisplayDuration(url: string): string {
    return this.recordingDurations[url] ?? '00:00';
  }

  playOrPauseRecording(url: string): void {
    if (!url) return;
    if (this.currentPlayingUrl === url) {
      this.stopRecording();
      return;
    }
    this.stopRecording();
    this.audioElement = new Audio(url);
    this.audioElement.addEventListener('loadedmetadata', () => {
      const dur = this.audioElement?.duration;
      if (dur != null && !isNaN(dur)) {
        this.recordingDurations = { ...this.recordingDurations, [url]: this.formatAudioDuration(dur) };
        this._cdr.markForCheck();
      }
    });
    this.audioElement.addEventListener('ended', () => {
      this.currentPlayingUrl = null;
      this._cdr.markForCheck();
    });
    this.audioElement.addEventListener('error', () => {
      if (this.ignoreRecordingAudioErrors || this.currentPlayingUrl !== url) {
        return;
      }
      this._toastService.error('Unable to play recording.', 'Error');
      this.currentPlayingUrl = null;
      this._cdr.markForCheck();
    });
    this.currentPlayingUrl = url;
    this._cdr.markForCheck();
    this.audioElement.play().catch(() => {
      setTimeout(() => {
        if (this.ignoreRecordingAudioErrors || this.currentPlayingUrl !== url) {
          return;
        }
        if (this.audioElement && !this.audioElement.paused) {
          return;
        }
        this._toastService.error('Unable to play recording.', 'Error');
        this.currentPlayingUrl = null;
        this._cdr.markForCheck();
      }, 100);
    });
    this._cdr.markForCheck();
  }

  private stopRecording(): void {
    if (this.audioElement) {
      const el = this.audioElement;
      this.ignoreRecordingAudioErrors = true;
      this.currentPlayingUrl = null;
      this.audioElement = null;
      el.pause();
      el.removeAttribute('src');
      el.load();
      setTimeout(() => {
        this.ignoreRecordingAudioErrors = false;
      }, 200);
    } else {
      this.currentPlayingUrl = null;
    }
    this._cdr.markForCheck();
  }

  private formatAudioDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  private emitTimelineMerged(): void {
    this.timelineMerged.emit();
  }

  /** Most recent row in the filtered merged timeline (feed sorts oldest → newest). */
  getLatestFilteredTimelineItem(): TimelineItem | null {
    const rows = this.filteredItems;
    if (!rows?.length) {
      return null;
    }
    return rows[rows.length - 1];
  }

  /** ISO instant for the newest merged timeline row (prospect CRM header / side panel). */
  getLatestFilteredTimelineItemIso(): string | null {
    return this.resolveTimelineItemIso(this.getLatestFilteredTimelineItem());
  }

  private resolveTimelineItemIso(item: TimelineItem | null): string | null {
    if (!item) {
      return null;
    }
    if (item.source === 'engage') {
      const a = item.data as EngageActivity;
      return (
        extractEngageActivityTimestampIso(a as Record<string, unknown>) ||
        String(a.createdDateTime || a.createdAt || '').trim() ||
        null
      );
    }
    if (item.source === 'activity') {
      const n = item.data as ActivityNoteDisplay;
      return String(n.createdAt || '').trim() || null;
    }
    if (item.source === 'sms') {
      const m = item.data as TwilioMessage;
      return String(m.createdAt || '').trim() || null;
    }
    if (item.source === 'event') {
      return this.getEventDateAndTimeIso(item.data as EngageEventByReferralItem) || null;
    }
    return null;
  }

  /**
   * Referral overview tile badge: New Request (`form_filled`) comment only — same line as the activity feed card body.
   */
  resolveReferralNoteBodyForOverviewTile(referralId: string): string {
    const rid = String(referralId || '').trim();
    if (!rid || !this.filteredItems?.length) {
      return '';
    }

    const engageReferralId = (a: EngageActivity): string =>
      String(a.referralId ?? a['referral_id'] ?? '').trim();

    const engageTyp = (a: EngageActivity): string =>
      String(a.type || '').toLowerCase().trim().replace(/\s+/g, '_');

    const isUsableMeta = (body: string): boolean =>
      !!body && !isGenericReferralOverviewMetaLabel(body);

    /** Earliest New Request for this referral (add-referral Comment). */
    for (let i = 0; i < this.filteredItems.length; i++) {
      const item = this.filteredItems[i];
      if (item.source !== 'engage') {
        continue;
      }
      const a = item.data as EngageActivity;
      if (engageTyp(a) !== 'form_filled') {
        continue;
      }
      const aRid = engageReferralId(a);
      if (aRid && aRid !== rid) {
        continue;
      }
      const body = this.stripProspectSideHtml(this.getEngageActivityCardBody(a));
      if (!body || body === 'New Request') {
        continue;
      }
      if (isUsableMeta(body)) {
        return body;
      }
    }

    return '';
  }

  /**
   * Activity sidebar filter preset for a timeline row (matches {@link itemMatchesAnyCategory}).
   */
  resolveActivityFilterCategoriesForTimelineItem(
    item: TimelineItem | null
  ): ActivityFilterCategory[] {
    if (!item) {
      return [];
    }
    const all: ActivityFilterCategory[] = [
      'calls',
      'messages',
      'notes',
      'activity',
      'reminders',
      'events',
    ];
    return all.filter((cat) => this.itemMatchesAnyCategory(item, [cat]));
  }

  /** Footer composer preset when opening Activity from a prospect “Recent activity” row. */
  resolveActivityComposerModeForTimelineItem(
    item: TimelineItem | null
  ): 'notes' | 'sms' | null {
    if (!item || item.source !== 'sms') {
      return null;
    }
    const msg = item.data as TwilioMessage;
    if (msg.callType || this.isOutboundCall(msg)) {
      return null;
    }
    return 'sms';
  }

  /**
   * Plain-text summary for Engage prospect side “Recent activity” (matches unified row semantics; no HTML).
   * {@link headline} is the semibold activity label; {@link headlineDetail} is muted secondary text (message, duration, date, etc.).
   */
  summarizeForProspectSidePanel(item: TimelineItem | null): {
    headline: string;
    headlineDetail?: string;
    when: string;
    actor: string;
    iconSrc: string;
  } | null {
    if (!item) {
      return null;
    }
    if (item.source === 'engage') {
      const a = item.data as EngageActivity;
      if (this.isEngageJoinConversationRow(a)) {
        const iso =
          extractEngageActivityTimestampIso(a as Record<string, unknown>) ||
          String(a.createdAt || a.createdDateTime || '').trim();
        return {
          headline: `${this.getEngageJoinDisplayName(a)} joined the conversation`,
          when: this.formatProspectSidePanelWhen(iso),
          actor: '',
          iconSrc: this.resolveProspectSidePanelIcon(item),
        };
      }
      const typNorm = String(a.type || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_');
      const iso =
        extractEngageActivityTimestampIso(a as Record<string, unknown>) ||
        String(a.createdDateTime || a.createdAt || '').trim();
      let titleParts: { headline: string; headlineDetail?: string };
      if (typNorm === 'reminder') {
        titleParts = this.buildProspectSidePanelTitle(
          'Reminder',
          this.stripProspectSideHtml(String(a.notes ?? a.comment ?? ''))
        );
      } else if (typNorm === 'stage') {
        const st = this.getStageDisplayName(String(a.stage || '').trim());
        const notes = this.stripProspectSideHtml(String(a.notes || ''));
        titleParts = this.buildProspectSidePanelTitle(
          'Stage',
          notes ? `${st} — ${notes}` : st
        );
      } else {
        const body = this.stripProspectSideHtml(this.getEngageActivityCardBody(a));
        if (this.shouldShowEngageTypeLabel(a)) {
          titleParts = this.buildProspectSidePanelTitle(this.getEngageTypeLabel(a), body);
        } else {
          titleParts = this.buildProspectSidePanelTitle(body || 'Note');
        }
      }
      const actor =
        typNorm === 'reminder'
          ? this.getReminderUnifiedTitle(a)
          : this.getEngageLeftColumnTitle(a).trim() || String(a.activityBy || '').trim();
      return {
        ...titleParts,
        when: this.formatProspectSidePanelWhen(iso),
        actor,
        iconSrc: this.resolveProspectSidePanelIcon(item),
      };
    }
    if (item.source === 'activity') {
      const n = item.data as ActivityNoteDisplay;
      const notes = this.stripProspectSideHtml(String(n.notes || ''));
      const activityType = this.stripProspectSideHtml(String(n.activityType || ''));
      const iso = String(n.createdAt || '').trim();
      return {
        ...this.buildProspectSidePanelTitle('Note', notes || activityType),
        when: this.formatProspectSidePanelWhen(iso),
        actor: String(n.activityBy || '').trim(),
        iconSrc: this.resolveProspectSidePanelIcon(item),
      };
    }
    if (item.source === 'sms') {
      const m = item.data as TwilioMessage;
      const body = this.stripProspectSideHtml(String(m.body || ''));
      const iso = String(m.createdAt || '').trim();
      return {
        ...this.buildProspectSidePanelTitle(this.getProspectSideSmsLabel(m), body),
        when: this.formatProspectSidePanelWhen(iso),
        actor: this.getMessageDisplayName(m),
        iconSrc: this.resolveProspectSidePanelIcon(item),
      };
    }
    if (item.source === 'event') {
      const e = item.data as EngageEventByReferralItem;
      const iso = this.getEventDateAndTimeIso(e);
      const eventType = this.getEventTypeDisplay(e);
      return {
        ...this.buildProspectSidePanelTitle(eventType || 'Event'),
        when: this.formatProspectSidePanelWhen(iso),
        actor: '',
        iconSrc: this.getEventIconSrc(),
      };
    }
    return null;
  }

  /** Semibold label + optional muted detail for prospect side “Recent activity”. */
  private buildProspectSidePanelTitle(
    label: string,
    detail?: string | null
  ): { headline: string; headlineDetail?: string } {
    const headline = String(label || '').trim() || 'Activity';
    const headlineDetail = this.stripProspectSideHtml(detail);
    if (!headlineDetail) {
      return { headline };
    }
    /** e.g. Scheduled with notes `06/23/2026` — match unified feed `Type: date` (not date alone). */
    if (this.isProspectSideDateOnlyDetail(headlineDetail)) {
      return { headline: `${headline}: ${headlineDetail}` };
    }
    return { headline, headlineDetail };
  }

  /** Detail is only a calendar date (no message body) — prospect side panel prefixes activity type. */
  private isProspectSideDateOnlyDetail(s: string): boolean {
    const t = String(s || '').trim();
    if (!t) {
      return false;
    }
    return (
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(t) ||
      /^\d{4}-\d{2}-\d{2}$/.test(t)
    );
  }

  /** Activity type label for prospect side SMS / call rows (matches unified timeline labels). */
  private getProspectSideSmsLabel(m: TwilioMessage): string {
    if (this.isOutboundCall(m)) {
      return 'Outbound Call';
    }
    if (String(m.callType || '').trim()) {
      return this.getReceivedSmsRowLabel(m);
    }
    return 'SMS';
  }

  private stripProspectSideHtml(s: string | null | undefined): string {
    return String(s || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private formatProspectSidePanelWhen(iso: string | null | undefined): string {
    const t = String(iso || '').trim();
    if (!t) {
      return '';
    }
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private resolveProspectSidePanelIcon(item: TimelineItem): string {
    if (item.source === 'event') {
      return this.getEventIconSrc();
    }
    if (item.source === 'sms') {
      return this.getSmsTimelineAvatarSrc(item.data as TwilioMessage);
    }
    const d = item.data as EngageActivity & ActivityNoteDisplay;
    let typ = this.timelineIconType(d);
    const eng = item.source === 'engage' ? (item.data as EngageActivity) : undefined;
    if (item.source === 'engage' && !typ) {
      typ = String(eng?.type || '').trim();
    }
    return this.getEngageIconLeft(typ, eng);
  }

}
